import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDemoStore } from '../store/demoStore';
import { PoiListManager } from '../components/PoiListManager';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Store, Plus, Users, Settings, Shield, Car, Network, Building, ChevronDown, ChevronRight, ChevronLeft, ChevronUp, KeyRound, X, Activity, Ban, ShieldCheck, Eye, RefreshCw, Cpu } from 'lucide-react';
// v0.4.9.4.6: the "AI Training" tab (LPR / Face Recognition / Wi-Fi CSI /
// Drone Detection). Mounted as a local-state tab, matching the three tabs
// already in this file — NOT as a nested route. The handover's suggested
// `<Route path="ai-training" …>` does not match how this component works.
// Both files are siblings under src/pages/ (this is src/pages/NetworkView.tsx,
// a file — there is no NetworkView/ directory).
import AiTraining from './AiTraining';
import { api } from '../services/api';
import Header from '../components/layout/Header';
import { AuthorizedPersonsManager, AuthorizedPlatesManager } from '../components/AuthorizedManagers';
// v0.4.9.2.4c4: shared bulk user-assign modal (also used by
// ClientDashboard). Reused rather than reimplemented.
import UserAddToStoresModal from '../components/UserAddToStoresModal';
import BulkPOIUpload from '../components/BulkPOIUpload';
import TrainingSimulation from '../components/TrainingSimulation';
// v0.4.6i: replaces the single 🔑 reset-password button with a
// 2-option dialog (Reset password / Reset device).
import { AdminResetDialog } from '../components/AdminResetDialog';
// v0.4.9.2.4c2: canonical country + city pickers for the Civil
// Respondent create/edit forms. Replaces the free-text inputs that
// let "ES" / "Spain" / "ESP" all land in civil_respondents.country.
// Uses the same components the c22a admin pages already ship, so
// country/city data is consistent across the whole app.
import { SearchableSelect, countryListToOptions } from '../components/SearchableSelect';
import { COUNTRIES } from '../data/countries';
import { getSubdivisions, type Subdivision } from '../utils/iso3166-2';

// =====================================================================
// v0.4.9.2.2: Country + city name → standardized code lookup.
// Auto-fills the country_code (ISO 3166-1 alpha-3) and city_code (IATA)
// inputs in the Civil Respondent form when the iSentinel360 employee
// types a country/city name. User can manually override.
//
// To extend: add new countries/cities as needed. When the user types
// something that's already a code (e.g. "ESP", "MAD"), the lookup
// falls through to identity (returns the input as-is).
// =====================================================================
const COUNTRY_NAME_TO_CODE: Record<string, string> = {
  // Western Europe + Americas + APAC
  'Spain': 'ESP', 'France': 'FRA', 'United States': 'USA', 'USA': 'USA',
  'United Kingdom': 'GBR', 'UK': 'GBR', 'Germany': 'DEU', 'Italy': 'ITA',
  'Portugal': 'PRT', 'Mexico': 'MEX', 'Brazil': 'BRA', 'Argentina': 'ARG',
  'Colombia': 'COL', 'Chile': 'CHL', 'Peru': 'PER', 'Canada': 'CAN',
  'Australia': 'AUS', 'Japan': 'JPN', 'China': 'CHN', 'India': 'IND',
  'Netherlands': 'NLD', 'Belgium': 'BEL', 'Switzerland': 'CHE',
  'Austria': 'AUT', 'Sweden': 'SWE', 'Norway': 'NOR', 'Denmark': 'DNK',
  'Finland': 'FIN', 'Poland': 'POL', 'Czech Republic': 'CZE', 'Czechia': 'CZE',
  'Ireland': 'IRL', 'Greece': 'GRC', 'Turkey': 'TUR', 'New Zealand': 'NZL',
  'South Africa': 'ZAF',
  // Russia + East Asia
  'Russia': 'RUS', 'Russian Federation': 'RUS',
  'South Korea': 'KOR', 'Korea': 'KOR', 'Korea, Republic of': 'KOR',
  'North Korea': 'PRK', "Korea, Democratic People's Republic of": 'PRK',
  // CIS countries
  'Belarus': 'BLR', 'Venezuela': 'VEN', 'Armenia': 'ARM', 'Azerbaijan': 'AZE',
  'Kazakhstan': 'KAZ', 'Kyrgyzstan': 'KGZ', 'Kyrgyz Republic': 'KGZ',
  'Moldova': 'MDA', 'Republic of Moldova': 'MDA', 'Tajikistan': 'TJK',
  'Turkmenistan': 'TKM', 'Uzbekistan': 'UZB',
  // ISO alpha-2 fallthrough (so "ES", "FR" stay as "ES", "FR" instead of forcing alpha-3)
  'ES': 'ESP', 'FR': 'FRA', 'US': 'USA', 'GB': 'GBR', 'DE': 'DEU',
  'IT': 'ITA', 'PT': 'PRT', 'MX': 'MEX', 'BR': 'BRA', 'AR': 'ARG',
  'CO': 'COL', 'CL': 'CHL', 'PE': 'PER', 'CA': 'CAN', 'AU': 'AUS',
  'JP': 'JPN', 'CN': 'CHN', 'IN': 'IND', 'NL': 'NLD', 'BE': 'BEL',
  'CH': 'CHE', 'AT': 'AUT', 'SE': 'SWE', 'NO': 'NOR', 'DK': 'DNK',
  'FI': 'FIN', 'PL': 'POL', 'CZ': 'CZE', 'IE': 'IRL', 'GR': 'GRC',
  'TR': 'TUR', 'NZ': 'NZL', 'ZA': 'ZAF', 'RU': 'RUS', 'KR': 'KOR',
  'KP': 'PRK', 'BY': 'BLR', 'VE': 'VEN', 'AM': 'ARM', 'AZ': 'AZE',
  'KZ': 'KAZ', 'KG': 'KGZ', 'MD': 'MDA', 'TJ': 'TJK', 'TM': 'TKM',
  'UZ': 'UZB',
};

// Top ~120 cities by passenger volume. Extend as new stations are added.
// Used for IATA auto-fill when employee types a city name.
const CITY_NAME_TO_CODE: Record<string, string> = {
  // Spain
  'Madrid': 'MAD', 'Barcelona': 'BCN', 'Valencia': 'VLC', 'Sevilla': 'SVQ',
  'Seville': 'SVQ', 'Málaga': 'AGP', 'Malaga': 'AGP', 'Bilbao': 'BIO',
  'Alicante': 'ALC', 'Granada': 'GRX', 'Zaragoza': 'ZAZ', 'Santiago de Compostela': 'SCQ',
  // France
  'Paris': 'CDG', 'Marseille': 'MRS', 'Lyon': 'LYS', 'Nice': 'NCE',
  'Toulouse': 'TLS', 'Bordeaux': 'BOD', 'Nantes': 'NTE',
  // UK
  'London': 'LHR', 'Manchester': 'MAN', 'Edinburgh': 'EDI', 'Birmingham': 'BHX',
  'Glasgow': 'GLA',
  // Germany
  'Berlin': 'BER', 'Munich': 'MUC', 'München': 'MUC', 'Frankfurt': 'FRA',
  'Hamburg': 'HAM', 'Cologne': 'CGN', 'Köln': 'CGN', 'Düsseldorf': 'DUS',
  'Stuttgart': 'STR',
  // Italy
  'Rome': 'FCO', 'Roma': 'FCO', 'Milan': 'MXP', 'Milano': 'MXP',
  'Naples': 'NAP', 'Napoli': 'NAP', 'Venice': 'VCE', 'Venezia': 'VCE',
  'Florence': 'FLR', 'Firenze': 'FLR',
  // Portugal
  'Lisbon': 'LIS', 'Lisboa': 'LIS', 'Porto': 'OPO',
  // USA
  'New York': 'JFK', 'Los Angeles': 'LAX', 'Chicago': 'ORD', 'Houston': 'IAH',
  'Miami': 'MIA', 'San Francisco': 'SFO', 'Boston': 'BOS', 'Washington': 'IAD',
  'Seattle': 'SEA', 'Atlanta': 'ATL', 'Dallas': 'DFW', 'Denver': 'DEN',
  // Mexico
  'Mexico City': 'MEX', 'Ciudad de México': 'MEX', 'Guadalajara': 'GDL',
  'Monterrey': 'MTY', 'Cancún': 'CUN', 'Cancun': 'CUN',
  // Brazil
  'São Paulo': 'GRU', 'Sao Paulo': 'GRU', 'Rio de Janeiro': 'GIG',
  'Brasília': 'BSB', 'Brasilia': 'BSB',
  // Argentina
  'Buenos Aires': 'EZE',
  // CIS
  'Moscow': 'SVO', 'Москва': 'SVO', 'Saint Petersburg': 'LED',
  'Minsk': 'MSQ', 'Минск': 'MSQ', 'Almaty': 'ALA', 'Алматы': 'ALA',
  'Astana': 'NQZ', 'Астана': 'NQZ', 'Yerevan': 'EVN', 'Ереван': 'EVN',
  'Baku': 'GYD', 'Баку': 'GYD', 'Tbilisi': 'TBS', 'Тбилиси': 'TBS',
  'Bishkek': 'FRU', 'Бишкек': 'FRU', 'Tashkent': 'TAS', 'Ташкент': 'TAS',
  'Chișinău': 'KIV', 'Chisinau': 'KIV', 'Dushanbe': 'DYU', 'Душанбе': 'DYU',
  'Ashgabat': 'ASB', 'Ашхабад': 'ASB', 'Caracas': 'CCS',
  // China
  'Beijing': 'PEK', 'Peking': 'PEK', 'Shanghai': 'PVG', 'Guangzhou': 'CAN',
  'Shenzhen': 'SZX', 'Chengdu': 'CTU', 'Hong Kong': 'HKG',
  // Japan
  'Tokyo': 'HND', 'Osaka': 'KIX', 'Kyoto': 'KIX',
  // Korea
  'Seoul': 'ICN', 'Busan': 'PUS',
  // India
  'Mumbai': 'BOM', 'Bombay': 'BOM', 'Delhi': 'DEL', 'Bangalore': 'BLR',
  'Bengaluru': 'BLR', 'Chennai': 'MAA', 'Kolkata': 'CCU', 'Calcutta': 'CCU',
};

/**
 * Look up a country code from a country name or 2-letter code.
 * If the input is already a 3-letter ISO code that's not in the map
 * (e.g. "TWN" for Taiwan), return it as-is.
 */
function resolveCountryCode(input: string): string {
  if (!input) return '';
  const trimmed = input.trim();
  const upper = trimmed.toUpperCase();
  if (COUNTRY_NAME_TO_CODE[trimmed]) return COUNTRY_NAME_TO_CODE[trimmed];
  if (COUNTRY_NAME_TO_CODE[upper])   return COUNTRY_NAME_TO_CODE[upper];
  // Already looks like a code (3 uppercase letters) — pass through
  if (/^[A-Z]{3}$/.test(upper))      return upper;
  // 2-letter code that wasn't in our map — keep as-is, don't force alpha-3
  if (/^[A-Z]{2}$/.test(upper))      return upper;
  return upper;
}

/**
 * Look up an IATA city code from a city name. If the input is already
 * a 3-letter code, return it as-is.
 */
function resolveCityCode(input: string): string {
  if (!input) return '';
  const trimmed = input.trim();
  const upper = trimmed.toUpperCase();
  if (CITY_NAME_TO_CODE[trimmed]) return CITY_NAME_TO_CODE[trimmed];
  if (CITY_NAME_TO_CODE[upper])   return CITY_NAME_TO_CODE[upper];
  if (/^[A-Z]{3}$/.test(upper))   return upper;
  return upper;
}


interface ClientData {
  id: string;
  name: string;
  nif?: string;
  training_mode_enabled?: boolean;
}

interface StoreData {
  id: string;
  code: string;
  name: string;
  country_code: string;
  city_code: string;
  guardian_enabled: boolean;
  access_control_enabled: boolean;
  network_enabled: boolean;
  network_countries?: string[];
  max_guards: number;
  max_admins?: number;
  client_id?: string | null;
  created_at: string;
  admin_email?: string;
  edge_server_ip?: string;
  edge_server_port?: number;
  wifi_ssid?: string;
  // v0.4.9.1.12: provisioning
  store_number?: number | null;
  hostname?: string | null;
  provisioning_status?: 'pending' | 'provisioned' | 'failed' | 'reset';
  edge_provisioned_at?: string | null;
}

interface EdgeCamera {
  id: string;
  name: string;
  rtsp_url: string;
  role: 'access_person' | 'poi_person' | 'poi_vehicle' | 'poi_mix' | 'access_vehicle' | 'drone_detection';
  location: string;
  // POI Settings
  poi_similarity_threshold?: number;
  poi_min_face_score?: number;
  poi_cooldown_sec?: number;
  // Access Control Settings
  ac_similarity_threshold?: number;
  ac_min_face_score?: number;
  ac_cooldown_sec?: number;
  // Shared Settings
  min_bbox_ratio?: number;
  max_bbox_ratio?: number;
  capture_cooldown_sec?: number;
}

interface StoreUser {
  id: string;
  email: string;
  name: string;
  role: string;
  role_in_store: string;
  is_primary_sadmin?: boolean;
  training_enabled?: boolean;

}


// Main Component
export default function NetworkView() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  // v0.4.9.2.4c5 — NetworkView is an EMPLOYEE-ONLY surface: superadmin,
  // sadmin and tadmin. Client roles (cadmin, admin, guard) must never
  // reach it — it exposes cross-client stores, civil-respondent orgs and
  // their user rosters.
  //
  // Defence in depth: the /network route already gates this in App.tsx
  // and the API re-checks per endpoint, but a client-side guard means a
  // mis-wired route cannot leak the page shell.
  //
  // The `user &&` is deliberate. AuthContext hydrates `user` in a
  // useEffect, so on first render it is null; gating on `!user` would
  // bounce a legitimate employee before hydration completes (the same
  // race that made the /api/auth/me pre-flight a footgun in c17).
  // While `user` is null we render nothing and let the parent route
  // decide — no flash, no premature redirect.
  const EMPLOYEE_ROLES = ['superadmin', 'sadmin', 'tadmin'];
  const isEmployee = user ? EMPLOYEE_ROLES.includes(user.role) : null;
  useEffect(() => {
    if (isEmployee === false) navigate('/', { replace: true });
  }, [isEmployee, navigate]);
  if (isEmployee === false) return null;

  const [activeTab, setActiveTab] = useState<'commerce' | 'civil' | 'opsec' | 'aiTraining'>('commerce');
  const [clients, setClients] = useState<any[]>([]);
  const [expandedClientId, setExpandedClientId] = useState<string | null>(null);
  const [stores, setStores] = useState<StoreData[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [allCountries, setAllCountries] = useState<string[]>([]);
  const [updatingClientId, setUpdatingClientId] = useState<string | null>(null);
  const [poiListRefresh, setPoiListRefresh] = useState(0);
  const [userSearch, setUserSearch] = useState('');
  const [editTrainingEnabled, setEditTrainingEnabled] = useState(false);

  // Client modal
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [clientModalError, setClientModalError] = useState('');
  const [newClientName, setNewClientName] = useState('');
  const [newClientAddress, setNewClientAddress] = useState('');
  const [newClientNif, setNewClientNif] = useState('');
  const [newCadminEmail, setNewCadminEmail] = useState('');
  const [newCadminPassword, setNewCadminPassword] = useState('');
  const [admin1Email, setAdmin1Email] = useState('');
  const [admin1Password, setAdmin1Password] = useState('');
  const [admin2Email, setAdmin2Email] = useState('');
  const [admin2Password, setAdmin2Password] = useState('');
  const [localMax, setLocalMax] = useState<Record<string, { max_guards: number; max_admins: number }>>({});

  // Add Civil Responder modal
  const [showAddCivilOrgModal, setShowAddCivilOrgModal] = useState(false);
  const [civilOrgType, setCivilOrgType] = useState<'emergency_services' | 'law_enforcement'>('law_enforcement');
  const [civilOrgCountry, setCivilOrgCountry] = useState('');
  const [civilOrgCity, setCivilOrgCity] = useState('');
  // v0.4.9.2.4c2: standardized codes.
  //   country      → ISO 3166-1 alpha-3 ("ESP") — same convention as the
  //                  c22a admin pages. Previously this held free text
  //                  ("ES" / "Spain" / "ESP" all possible).
  //   country_code → same alpha-3, kept in sync for legacy consumers.
  //   city         → subdivision display name ("Madrid"). Kept as the
  //                  name because the org list + BOLO history render it
  //                  as text.
  //   city_code    → full ISO 3166-2 code ("ES-MAD"). Previously an
  //                  IATA 3-letter code ("MAD"); changed to the
  //                  ISO standard for consistency with c22a.
  // All four are now set together by the pickers — no manual typing.
  const [civilOrgCountryCode, setCivilOrgCountryCode] = useState('');
  const [civilOrgCityCode, setCivilOrgCityCode] = useState('');
  // Subdivision list for the ADD form, loaded when country changes.
  const [civilOrgSubs, setCivilOrgSubs] = useState<Subdivision[]>([]);
  const [civilOrgSubsLoading, setCivilOrgSubsLoading] = useState(false);
  // v0.4.9.2.4c2: set by the country picker so the legacy
  // resolveCountryCode auto-fill effect below doesn't overwrite the
  // canonical value the picker just wrote.
  const [civilOrgPickedCountry, setCivilOrgPickedCountry] = useState(false);
  const [civilOrgDivision, setCivilOrgDivision] = useState('');
  const [civilOrgAddress, setCivilOrgAddress] = useState('');
  const [civilOrgPhone, setCivilOrgPhone] = useState('');
  const [civilOrgContact, setCivilOrgContact] = useState('');
  // Default 50 — superadmin/sadmin can override when creating the org.
  // The civil respondent admin CANNOT exceed this when adding users.
  const [civilOrgMaxLicenses, setCivilOrgMaxLicenses] = useState<number>(50);
  // First user to seed (agentAdmin) — password is auto-generated and emailed
  const [civilAgentAdminEmail, setCivilAgentAdminEmail] = useState('');
  const [civilAgentAdminName, setCivilAgentAdminName] = useState('');

  // Civil Respondent list + Manage modal
  const [civilOrgs, setCivilOrgs] = useState<any[]>([]);
  const [civilOrgsLoading, setCivilOrgsLoading] = useState(false);
  const [managingCivilOrg, setManagingCivilOrg] = useState<any | null>(null);
  const [civilOrgUsers, setCivilOrgUsers] = useState<any[]>([]);
  const [civilOrgUsersLoading, setCivilOrgUsersLoading] = useState(false);
  const [resettingPasswordUserId, setResettingPasswordUserId] = useState<string | null>(null);
  const [addUserToOrg, setAddUserToOrg] = useState<{ orgId: string; show: boolean }>({ orgId: '', show: false });
  const [newOrgUserEmail, setNewOrgUserEmail] = useState('');
  const [newOrgUserName, setNewOrgUserName] = useState('');
  const [newOrgUserRole, setNewOrgUserRole] = useState<'agent' | 'agentAdmin'>('agent');
  const [newOrgUserError, setNewOrgUserError] = useState('');

  // Org edit form (in Manage modal)
  const [editOrgForm, setEditOrgForm] = useState({
    type: 'law_enforcement' as 'law_enforcement' | 'emergency_services',
    division_name: '',
    country: '',
    city: '',
    country_code: '',     // v0.4.9.2.2: ISO 3166-1 alpha-3
    city_code: '',        // v0.4.9.2.2: IATA city code
    address: '',
    phone: '',
    contact_person: '',
    max_licenses: 50,        // editable by superadmin/sadmin
    // `stations` is kept in state for backward-compat with the API
    // (the column exists in the DB from an earlier migration) but is
    // not user-editable in the UI — the org's identifier is its
    // division_name, not a station count. Defaults to 0.
    stations: 0,
  });
  const [editOrgError, setEditOrgError] = useState('');
  // v0.4.9.2.4c2: subdivision list for the EDIT form, loaded when the
  // form's country changes. Populated from the same ISO-3166-2 dataset
  // the ADD form uses.
  const [editOrgSubs, setEditOrgSubs] = useState<Subdivision[]>([]);
  const [editOrgSubsLoading, setEditOrgSubsLoading] = useState(false);
  const [editOrgSubmitting, setEditOrgSubmitting] = useState(false);
  const [civilOrgModalError, setCivilOrgModalError] = useState('');
  const [civilOrgSubmitting, setCivilOrgSubmitting] = useState(false);

  // v0.4.9.2.2: Auto-fill country_code + city_code from the country/city
  // names. We use a ref to track whether the user has manually edited the
  // code field — if not, the auto-fill runs; if they typed something
  // themselves, we don't override it on subsequent country/city changes.
  const countryCodeUserEdited = useRef(false);
  const cityCodeUserEdited = useRef(false);
  const editOrgCodeUserEdited = useRef(false);
  const editOrgCityCodeUserEdited = useRef(false);

  // =================================================================
  // v0.4.9.2.4c2: canonical country + city pickers.
  //
  // The ADD and EDIT Civil Respondent forms used to have four free-text
  // inputs (country, country_code, city, city_code) with hand-maintained
  // name→code maps. resolveCountryCode() passes anything through, so
  // "ES", "Spain" and "ESP" could all end up in civil_respondents.country
  // depending on what the operator typed. That is the source of the
  // inconsistent data the org-picker filter surfaces.
  //
  // Now the operator picks from a dropdown:
  //   country   → alpha-3 code, written to BOTH `country` and `country_code`
  //   city      → subdivision NAME, written to `city`
  //   city_code → full ISO 3166-2 code, written to `city_code`
  //
  // `country` holds the alpha-3 (not the display name) because that is
  // the convention locked in c22a for most_wanted / missing_persons /
  // stolen_vehicles, and bolos.js normalizeCountry() already accepts a
  // 3-letter uppercase value. The org LIST display maps alpha-3 back to
  // the readable name via countryLabel() below.
  // =================================================================

  /** Readable country name for an alpha-3 / alpha-2 / name value. */
  function countryLabel(raw: string | null | undefined): string {
    if (!raw) return '';
    const v = raw.trim();
    if (!v) return '';
    const up = v.toUpperCase();
    const hit = COUNTRIES.find(c => c.code === up || c.alpha2 === up);
    if (hit) return hit.name;
    // Legacy rows may hold a full name already ("Spain") — show as-is.
    return v;
  }

  /** ADD form: country picked. Sets country + country_code together. */
  function handleAddOrgCountryPick(alpha3: string) {
    setCivilOrgPickedCountry(true);
    countryCodeUserEdited.current = true;
    setCivilOrgCountry(alpha3);
    setCivilOrgCountryCode(alpha3);
    // Changing country invalidates the city (different subdivisions).
    setCivilOrgCity('');
    setCivilOrgCityCode('');
    cityCodeUserEdited.current = true;
  }

  /** ADD form: city picked. Sets city (name) + city_code (ISO-3166-2). */
  function handleAddOrgCityPick(code: string) {
    cityCodeUserEdited.current = true;
    setCivilOrgCityCode(code);
    const sub = civilOrgSubs.find(s => s.code === code);
    setCivilOrgCity(sub ? sub.name : code);
  }

  /** EDIT form: country picked. Same contract as the ADD form. */
  function handleEditOrgCountryPick(alpha3: string) {
    editOrgCodeUserEdited.current = true;
    setEditOrgForm(f => ({
      ...f,
      country: alpha3,
      country_code: alpha3,
      city: '',
      city_code: '',
    }));
    editOrgCityCodeUserEdited.current = true;
  }

  /** EDIT form: city picked. Same contract as the ADD form. */
  function handleEditOrgCityPick(code: string) {
    editOrgCityCodeUserEdited.current = true;
    const sub = editOrgSubs.find(s => s.code === code);
    setEditOrgForm(f => ({ ...f, city: sub ? sub.name : code, city_code: code }));
  }

  // Load the ADD form's subdivision list whenever its country changes.
  useEffect(() => {
    if (!civilOrgPickedCountry) return;  // don't fetch on a legacy free-text value
    if (!civilOrgCountry) {
      setCivilOrgSubs([]);
      return;
    }
    let cancelled = false;
    setCivilOrgSubsLoading(true);
    getSubdivisions(civilOrgCountry)
      .then(subs => {
        if (!cancelled) { setCivilOrgSubs(subs); setCivilOrgSubsLoading(false); }
      })
      .catch(() => { if (!cancelled) { setCivilOrgSubs([]); setCivilOrgSubsLoading(false); } });
    return () => { cancelled = true; };
  }, [civilOrgCountry, civilOrgPickedCountry]);

  // Load the EDIT form's subdivision list whenever its country changes.
  // Unlike the ADD form this does NOT gate on a "picked" flag: when the
  // modal opens for an existing org we want the city dropdown populated
  // immediately so the current city shows with its real label.
  useEffect(() => {
    const alpha3 = editOrgForm.country_code || editOrgForm.country;
    if (!alpha3 || alpha3.length !== 3) {
      setEditOrgSubs([]);
      return;
    }
    let cancelled = false;
    setEditOrgSubsLoading(true);
    getSubdivisions(alpha3.toUpperCase())
      .then(subs => { if (!cancelled) { setEditOrgSubs(subs); setEditOrgSubsLoading(false); } })
      .catch(() => { if (!cancelled) { setEditOrgSubs([]); setEditOrgSubsLoading(false); } });
    return () => { cancelled = true; };
  }, [editOrgForm.country, editOrgForm.country_code]);

  useEffect(() => {
    if (!countryCodeUserEdited.current && civilOrgCountry) {
      const autoCode = resolveCountryCode(civilOrgCountry);
      if (autoCode && autoCode !== civilOrgCountryCode) {
        setCivilOrgCountryCode(autoCode);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [civilOrgCountry]);

  useEffect(() => {
    if (!cityCodeUserEdited.current && civilOrgCity) {
      const autoCode = resolveCityCode(civilOrgCity);
      if (autoCode && autoCode !== civilOrgCityCode) {
        setCivilOrgCityCode(autoCode);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [civilOrgCity]);

  // Same auto-fill for the EDIT form (resets each time the modal opens)
  useEffect(() => {
    if (!editOrgCodeUserEdited.current && editOrgForm.country) {
      const autoCode = resolveCountryCode(editOrgForm.country);
      if (autoCode && autoCode !== editOrgForm.country_code) {
        setEditOrgForm(f => ({ ...f, country_code: autoCode }));
      }
    }
    if (!editOrgCityCodeUserEdited.current && editOrgForm.city) {
      const autoCode = resolveCityCode(editOrgForm.city);
      if (autoCode && autoCode !== editOrgForm.city_code) {
        setEditOrgForm(f => ({ ...f, city_code: autoCode }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editOrgForm.country, editOrgForm.city]);

  // Manage store dialog
  const [selectedStore, setSelectedStore] = useState<StoreData | null>(null);
  // v0.4.9.2.4c5 (TAdmin) — the store Management modal is split into two
  // tabs. A TAdmin only ever sees "Provisioning"; the "Users/POIs" tab is
  // not rendered for them at all, so they never see the client's guard
  // roster, authorized faces or license plates.
  //
  // The split is mirrored server-side (canManageStoreClientData in
  // stores.js, tadminWriteDenied in pois.js) — the tab is the UI half of
  // a boundary that is enforced on the API regardless.
  const [storeTab, setStoreTab] = useState<'provisioning' | 'data'>('provisioning');
  const isTadmin = user?.role === 'tadmin';
  const canSeeStoreData = !isTadmin;
  // v0.4.9.2.4c5 — component-level role flags. Needed by the create-user
  // form's Global Role dropdown, which sits outside the user-list map
  // where these were previously declared locally.
  const isSuperadmin = user?.role === 'superadmin';
  const isSadmin = user?.role === 'sadmin';
  // v0.4.9.2.4c5: bulk user assignment toggle. The modal itself is the
  // SHARED UserAddToStoresModal (also used by ClientDashboard) rather than
  // a second implementation — one pattern, one set of bugs.
  const [showBulkAssign, setShowBulkAssign] = useState(false);
  const [bulkRole, setBulkRole] = useState<'tadmin' | 'sadmin' | 'admin' | 'guard'>('tadmin');

  const [storeUsers, setStoreUsers] = useState<StoreUser[]>([]);
  const [storePoiCodes, setStorePoiCodes] = useState<string[]>([]);
  const [authorizedRefreshKey, setAuthorizedRefreshKey] = useState(0);

  // ----- OPSEC tab state (sadmin / superadmin only) -----
  const [opsecSnapshot, setOpsecSnapshot] = useState<any | null>(null);
  const [opsecLoading, setOpsecLoading] = useState(false);
  const [opsecError, setOpsecError] = useState<string | null>(null);
  const [opsecBlockIp, setOpsecBlockIp] = useState('');
  const [opsecBlockReason, setOpsecBlockReason] = useState('');
  const [opsecBlocking, setOpsecBlocking] = useState(false);
  const [opsecBlockMessage, setOpsecBlockMessage] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userRole, setUserRole] = useState<'guard' | 'admin'>('guard');
  // v0.4.9.2.4c5 — GLOBAL role (users.role) for the user being created.
  // Separate from `userRole` (the per-store Local Role). Defaults to
  // matching the Local Role so the common case stays one field's worth
  // of thought; an employee can widen it to create a TAdmin without the
  // create-then-promote dance. The server enforces who may set an
  // elevated value — this dropdown is only the affordance.
  const [userGlobalRole, setUserGlobalRole] = useState<string>('guard');
  const [error, setError] = useState<string | null>(null);
  const [modalError, setModalError] = useState('');
  const [success, setSuccess] = useState('');
  // v0.4.6i: state for the reset-access dialog (used at the
  // commerce-user reset site and the civil-respondent reset site).
  const [resetTarget, setResetTarget] = useState<{ userId: string; userName: string; userEmail: string } | null>(null);

  const [addingUser, setAddingUser] = useState(false);  // v0.4.6: prevents double-submit
  const [cameras, setCameras] = useState<EdgeCamera[]>([]);
  const [editingCamera, setEditingCamera] = useState<EdgeCamera | null>(null);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [cameraForm, setCameraForm] = useState<EdgeCamera>({
    id: '',
    name: '',
    rtsp_url: '',
    role: 'access_person' as EdgeCamera['role'], // keep a literal type if strict
    location: '',
    poi_similarity_threshold: 0.47,
    poi_min_face_score: 0.86,
    poi_cooldown_sec: 30,
    ac_similarity_threshold: 0.47,
    ac_min_face_score: 0.86,
    ac_cooldown_sec: 45,
    min_bbox_ratio: 0.55,
    max_bbox_ratio: 1.65,
    capture_cooldown_sec: 60,
  });

  // Load cameras from VPS whenever the selected store changes.
  // Until now this was local-only state with a TODO; now it hits /api/cameras.
  useEffect(() => {
    if (!selectedStore) {
      setCameras([]);
      return;
    }
    api.getCameras(selectedStore.id)
      .then((data) => {
        // Normalize the API response into the local EdgeCamera shape.
        // The API returns rows with id, name, rtsp_url, role, location, enabled, sort_order, version.
        setCameras(
          (data || []).map((c: any) => ({
            id: c.id,
            name: c.name,
            rtsp_url: c.rtsp_url,
            role: c.role || 'person',
            location: c.location || '',
            poi_similarity_threshold: c.poi_similarity_threshold,
            poi_min_face_score: c.poi_min_face_score,
            poi_cooldown_sec: c.poi_cooldown_sec,
            ac_similarity_threshold: c.ac_similarity_threshold,
            ac_min_face_score: c.ac_min_face_score,
            ac_cooldown_sec: c.ac_cooldown_sec,
            min_bbox_ratio: c.min_bbox_ratio,
            max_bbox_ratio: c.max_bbox_ratio,
            capture_cooldown_sec: c.capture_cooldown_sec,
          }))
        );
      })
      .catch((err) => {
        console.error('[iSentinel360] Failed to load cameras:', err);
        setCameras([]);
      });
  }, [selectedStore?.id]);

  // Edit store parameter

  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editRole, setEditRole] = useState<'guard' | 'admin' | 'cadmin'>('guard');
  // v0.4.9.2.4c5: 'tadmin' added to the global-role union.
  const [editGlobalRole, setEditGlobalRole] = useState<'guard' | 'admin' | 'cadmin' | 'sadmin' | 'tadmin' | 'superadmin'>('guard');

  // Fetch clients (employee roles only)
  // v0.4.9.2.4c11: 'tadmin' added. Without it the TAdmin's `clients`
  // state stayed [], so getClientName() fell through to 'Unknown Client'
  // for every store — the TAdmin was navigating blind.
  useEffect(() => {
    if (user?.role === 'superadmin' || user?.role === 'sadmin' || user?.role === 'tadmin') {
      api.getClients().then(setClients).catch(console.error);
    }
  }, [user]);


// Fixing hierarchy (superadmin only)
useEffect(() => {
  if (editGlobalRole === 'guard') {
    setEditRole('guard');
  } else if (editGlobalRole === 'admin' || editGlobalRole === 'cadmin') {
    setEditRole('admin');
  } else if (editGlobalRole === 'tadmin') {
    // v0.4.9.2.4c5: TAdmin provisions Edge Servers and installs Cameras,
    // which is store-scoped work — so their per-store role must be
    // 'admin', never 'guard'.
    setEditRole('admin');
  }
}, [editGlobalRole]);

// Keeping localMax in sync with stores
useEffect(() => {
  const synced: Record<string, { max_guards: number; max_admins: number }> = {};
  stores.forEach(s => {
    synced[s.id] = { max_guards: s.max_guards, max_admins: s.max_admins ?? 2 };
  });
  setLocalMax(synced);
}, [stores]);

  // Form for adding new store
  const [newStore, setNewStore] = useState({
    name: '',
    country_code: '',
    city_code: '',
    admin_email: '',
    client_id: '',
    guardian_enabled: true,
    access_control_enabled: false,
    network_enabled: false,
    max_guards: 4,
    max_admins: 2,
    // v0.4.9.1.12: provisioning
    store_number: null as number | null,
    hostname: '',
  });

  // v0.4.9.1.12: provisioning state
  const [proposedHostname, setProposedHostname] = useState('');
  const [hostnameAvailable, setHostnameAvailable] = useState(true);
  const [checkingHostname, setCheckingHostname] = useState(false);
  const [provisioningInfo, setProvisioningInfo] = useState<{
    setup_url?: string;
    provisioning_token?: string;
    expires_at?: string;
  } | null>(null);

  // v0.4.9.1.12 hotfix v8: poll for provisioning status while modal is open.
  // Holds the interval ID so we can clear it once provisioning completes.
  const provisioningPollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Cleanup the polling interval when the component unmounts or the modal closes.
  useEffect(() => {
    return () => {
      if (provisioningPollIntervalRef.current) clearInterval(provisioningPollIntervalRef.current);
    };
  }, []);

  const fetchStores = useCallback(async () => {
    try {
      const data = await api.getStores();
      setStores(data);
    } catch (err: any) { 
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStores(); }, [fetchStores]);

  // v0.4.9.1.12: when country+city are set in newStore, fetch next available number
  useEffect(() => {
    if (!newStore.country_code || !newStore.city_code || newStore.country_code.length < 2 || newStore.city_code.length < 2) {
      setProposedHostname('');
      setHostnameAvailable(true);
      return;
    }
    let cancelled = false;
    api.getNextStoreNumber(newStore.country_code, newStore.city_code)
      .then(({ next_number, proposed_hostname }) => {
        if (cancelled) return;
        setProposedHostname(proposed_hostname);
        // Auto-populate if user hasn't manually edited
        setNewStore(prev => ({
          ...prev,
          store_number: prev.store_number == null ? next_number : prev.store_number,
          hostname: prev.hostname === '' ? proposed_hostname : prev.hostname
        }));
        setHostnameAvailable(true);
      })
      .catch(() => {
        if (!cancelled) {
          setProposedHostname('');
        }
      });
    return () => { cancelled = true; };
  }, [newStore.country_code, newStore.city_code]);

  // v0.4.9.1.12: when store_number changes manually, re-derive hostname + check uniqueness
  useEffect(() => {
    if (!newStore.country_code || !newStore.city_code || newStore.store_number == null) {
      setHostnameAvailable(true);
      return;
    }
    const candidate = `${newStore.country_code.toLowerCase()}-${newStore.city_code.toLowerCase()}-${String(newStore.store_number).padStart(6, '0')}.isentinel360`;
    setNewStore(prev => ({ ...prev, hostname: candidate }));
    setCheckingHostname(true);
    let cancelled = false;
    api.checkStoreNumber(newStore.country_code, newStore.city_code, newStore.store_number)
      .then(({ available }) => {
        if (!cancelled) setHostnameAvailable(available);
      })
      .catch(() => {
        if (!cancelled) setHostnameAvailable(true); // assume OK on error
      })
      .finally(() => {
        if (!cancelled) setCheckingHostname(false);
      });
    return () => { cancelled = true; };
  }, [newStore.store_number, newStore.country_code, newStore.city_code]);

  // Auto‑select the first store of the selected client (for admin/guard views)
  /*
  useEffect(() => {
    if (selectedClientId && stores.length > 0) {
      const firstStore = stores.find(s => s.client_id === selectedClientId);
      if (firstStore && !selectedStore) {
        setSelectedStore(firstStore);
        fetchStoreUsers(firstStore.id);
        api.getStorePois(firstStore.id).then(data => {
          setStorePoiCodes(data.map((p: any) => p.code));
        }).catch(() => setStorePoiCodes([]));
      }
    }
  }, [selectedClientId, stores]);
  */

  useEffect(() => {
    const codes = [...new Set(stores.map(s => s.country_code).filter(Boolean))];
    setAllCountries(codes);
  }, [stores]);

  const getClientName = (clientId: string) => {
    if (clientId === '__none__') return 'No Client';
    const client = clients.find(c => c.id === clientId);
    return client ? client.name : 'Unknown Client';
  };

  // Build a list of client entries (including those with zero stores)
const buildClientEntries = useCallback(() => {
  const entries: { clientId: string; name: string; stores: StoreData[]; training_mode_enabled?: boolean }[] = [];
  const seen = new Set<string>();

  // Add entries from clients state
  for (const client of clients) {
    if (!seen.has(client.id)) {
      entries.push({
        clientId: client.id,
        name: client.name,
        stores: [],
        training_mode_enabled: client.training_mode_enabled ?? false,
      });
      seen.add(client.id);
    }
  }

  const storeMap: Record<string, StoreData[]> = {};
  for (const store of stores) {
    const cid = store.client_id || '__none__';
    if (!storeMap[cid]) storeMap[cid] = [];
    storeMap[cid].push(store);
    if (!seen.has(cid)) {
      entries.push({
        clientId: cid,
        name: getClientName(cid),
        stores: [],
        training_mode_enabled: false, // orphan stores have no client, disable toggle
      });
      seen.add(cid);
    }
  }

  // Merge stores into entries
  for (const entry of entries) {
    entry.stores = storeMap[entry.clientId] || [];
  }

  return entries;
}, [clients, stores]);

  const clientEntries = buildClientEntries();

  // Store user management
  const fetchStoreUsers = async (storeId: string) => {
    try {
      const users = await api.getStoreUsers(storeId);
      setStoreUsers(users);
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddUser = async (storeId: string) => {
    if (!userEmail) return;
    if (addingUser) return;  // v0.4.6: ignore re-entry while in flight
    setAddingUser(true);
    setModalError('');
    try {
      // v0.4.9.2.4c5: pass the global role. Keep the Local Role in the
      // store_users row, which the per-store grant is derived from.
      await api.addStoreUser(storeId, userEmail, userRole, userName, undefined, userGlobalRole);
      setUserEmail(''); setUserName('');
      // Reset both dropdowns together so the next add starts clean.
      setUserRole('guard'); setUserGlobalRole('guard');
      fetchStoreUsers(storeId);
      setSuccess('User added');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err: any) {
      setModalError(err.message || 'Failed to add user');
    } finally {
      setAddingUser(false);
    }
  };

  const handleRemoveUser = async (storeId: string, userId: string) => {
    if (!window.confirm(t('admin.confirmRemoveUser') || 'Remove this user?')) return;
    try {
      await api.removeStoreUser(storeId, userId);
      fetchStoreUsers(storeId);
      setSuccess('User removed');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err: any) { setError(err.message); }
  };

  const handleToggleFeature = async (storeId: string, field: string, value: boolean | number) => {
    try {
      await api.updateStore(storeId, { [field]: value });
      fetchStores();
      setSuccess('Updated');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err: any) {
      setError(err.message);
    }
  };

  // Create client
  const handleCreateClient = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newClientName.trim()) return;
    setClientModalError('');
    try {
      await api.createClient({ name: newClientName, address: newClientAddress, nif: newClientNif, cadmin_email: newCadminEmail, cadmin_password: newCadminPassword });
      setShowAddClientModal(false);
      // reset form
      setNewClientName(''); setNewClientAddress(''); setNewClientNif(''); setNewCadminEmail(''); setNewCadminPassword('');
      // Re‑fetch clients
      const updatedClients = await api.getClients();
      setClients(updatedClients);
      setSuccess('Client created');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err: any) {
      setClientModalError(err.message || 'Failed to create client');
    }
  };

  const handleCreateCivilOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!civilOrgDivision.trim() || !civilOrgCountry.trim()) {
      setCivilOrgModalError('Division name and country are required');
      return;
    }
    if (!civilAgentAdminEmail || !civilAgentAdminName) {
      setCivilOrgModalError('First agentAdmin email and name are required');
      return;
    }
    setCivilOrgSubmitting(true);
    setCivilOrgModalError('');
    try {
      // Create the org
      const org = await api.createCivilRespondent({
        type: civilOrgType,
        country: civilOrgCountry,
        country_code: civilOrgCountryCode || null,   // v0.4.9.2.2
        city: civilOrgCity || null,
        city_code: civilOrgCityCode || null,         // v0.4.9.2.2
        division_name: civilOrgDivision,
        address: civilOrgAddress || null,
        phone: civilOrgPhone || null,
        contact_person: civilOrgContact || null,
        max_licenses: civilOrgMaxLicenses,  // license allowance, default 50
      });
      // Seed the first agentAdmin user (password is auto-generated and emailed)
      if (org?.id) {
        await api.createCivilRespondentUser(org.id, {
          email: civilAgentAdminEmail,
          name: civilAgentAdminName,
          role: 'agentAdmin',
        });
      }
      setShowAddCivilOrgModal(false);
      // reset
      setCivilOrgDivision(''); setCivilOrgCountry(''); setCivilOrgCity('');
      // v0.4.9.2.4c2: also reset the picker-derived state so a stale
      // subdivision list / "country was picked" flag doesn't leak into
      // the next time the operator opens the ADD modal.
      setCivilOrgCountryCode(''); setCivilOrgCityCode('');
      setCivilOrgSubs([]); setCivilOrgSubsLoading(false);
      setCivilOrgPickedCountry(false);
      countryCodeUserEdited.current = false;
      cityCodeUserEdited.current = false;
      setCivilOrgAddress(''); setCivilOrgPhone(''); setCivilOrgContact('');
      setCivilAgentAdminEmail(''); setCivilAgentAdminName('');
      setCivilOrgMaxLicenses(50);
      setSuccess('Civil respondent org created. Welcome email sent with temp password.');
      setTimeout(() => setSuccess(''), 3000);
      // Refresh the civil org list
      loadCivilOrgs();
    } catch (err: any) {
      setCivilOrgModalError(err.message || 'Failed to create civil respondent org');
    } finally {
      setCivilOrgSubmitting(false);
    }
  };

  // ---- Civil Respondents list ----
  const loadCivilOrgs = useCallback(async () => {
    setCivilOrgsLoading(true);
    try {
      const list = await api.listCivilRespondents();
      setCivilOrgs(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Failed to load civil orgs:', err);
      setCivilOrgs([]);
    } finally {
      setCivilOrgsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'civil') {
      loadCivilOrgs();
    }
  }, [activeTab, loadCivilOrgs]);

  // ----- OPSEC tab: load snapshot when tab opens -----
  // Role gating is handled by the /network route AND the backend's
  // requireOpsAdmin middleware. We do NOT gate here on `user?.role`
  // because the AuthContext hydrates user in a useEffect — if the OPSEC
  // tab is opened in the same tick, user is still null and a local
  // gate would exit before fetch. Let the API decide.
  const loadOpsecSnapshot = useCallback(async () => {
    setOpsecLoading(true);
    setOpsecError(null);
    try {
      const data = await api.opsecGetSnapshot();
      setOpsecSnapshot(data);
    } catch (err: any) {
      setOpsecError(err?.message || 'Failed to load OPSEC status');
    } finally {
      setOpsecLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'opsec') {
      loadOpsecSnapshot();
    }
  }, [activeTab, loadOpsecSnapshot]);

  // Quick block: only the most common action, callable straight from the tab.
  async function handleOpsecQuickBlock(e: React.FormEvent) {
    e.preventDefault();
    const ip = opsecBlockIp.trim();
    if (!ip) {
      setOpsecBlockMessage({ kind: 'err', text: t('network.opsecIpRequired') || 'IP is required' });
      return;
    }
    setOpsecBlocking(true);
    setOpsecBlockMessage(null);
    try {
      const res = await api.opsecBlockIp(ip, opsecBlockReason.trim() || undefined);
      setOpsecBlockMessage({ kind: 'ok', text: res.message || (t('network.opsecBlockOk') || 'IP blocked') });
      setOpsecBlockIp('');
      setOpsecBlockReason('');
      // Refresh the snapshot so the new block count shows up immediately.
      loadOpsecSnapshot();
    } catch (err: any) {
      setOpsecBlockMessage({ kind: 'err', text: err?.message || (t('network.opsecBlockErr') || 'Failed to block IP') });
    } finally {
      setOpsecBlocking(false);
    }
  }

  const openManageCivilOrg = async (org: any) => {
    setManagingCivilOrg(org);
    // v0.4.9.2.2: reset the user-edited flags so the auto-fill effect
    // runs once for any blank code field the org might have.
    editOrgCodeUserEdited.current = false;
    editOrgCityCodeUserEdited.current = false;
    // Pre-fill the edit form with the org's current values.
    setEditOrgForm({
      type: org.type || 'law_enforcement',
      division_name: org.division_name || '',
      country: org.country || '',
      country_code: org.country_code || '',   // v0.4.9.2.2
      city: org.city || '',
      city_code: org.city_code || '',         // v0.4.9.2.2
      address: org.address || '',
      phone: org.phone || '',
      contact_person: org.contact_person || '',
      max_licenses: org.max_licenses ?? 50,
      stations: org.stations ?? 0,
    });
    setEditOrgError('');
    setCivilOrgUsers([]);
    setCivilOrgUsersLoading(true);
    try {
      const users = await api.listCivilRespondentUsers(org.id);
      setCivilOrgUsers(Array.isArray(users) ? users : []);
    } catch (err) {
      console.error('Failed to load users:', err);
      setCivilOrgUsers([]);
    } finally {
      setCivilOrgUsersLoading(false);
    }
  };

  const closeManageCivilOrg = () => {
    setManagingCivilOrg(null);
    setCivilOrgUsers([]);
    setAddUserToOrg({ orgId: '', show: false });
    setNewOrgUserEmail('');
    setNewOrgUserName('');
    setNewOrgUserRole('agent');
    setNewOrgUserError('');
    setEditOrgError('');
  };

  const handleUpdateCivilOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!managingCivilOrg) return;
    if (!editOrgForm.division_name.trim() || !editOrgForm.country.trim()) {
      setEditOrgError(t('network.civilOrgDivision') + ' & ' + t('network.civilOrgCountry') + ' required');
      return;
    }
    setEditOrgSubmitting(true);
    setEditOrgError('');
    try {
      const updated = await api.updateCivilRespondent(managingCivilOrg.id, {
        type: editOrgForm.type,
        division_name: editOrgForm.division_name,
        country: editOrgForm.country.toUpperCase(),
        country_code: editOrgForm.country_code || null,   // v0.4.9.2.2
        city: editOrgForm.city || null,
        city_code: editOrgForm.city_code || null,         // v0.4.9.2.2
        address: editOrgForm.address || null,
        phone: editOrgForm.phone || null,
        contact_person: editOrgForm.contact_person || null,
        max_licenses: editOrgForm.max_licenses,
        stations: editOrgForm.stations,
      });
      // Update the local org in state and close
      setManagingCivilOrg(updated);
      setSuccess(t('network.orgUpdated') || 'Organization updated.');
      setTimeout(() => setSuccess(''), 2500);
      // Refresh the list
      loadCivilOrgs();
    } catch (err: any) {
      setEditOrgError(err.message || 'Failed to update organization');
    } finally {
      setEditOrgSubmitting(false);
    }
  };

  const handleResetPassword = async (userId: string) => {
    if (!managingCivilOrg) return;
    if (!confirm(t('network.confirmResetPassword') || "Reset this user's password? A new password will be emailed.")) return;
    setResettingPasswordUserId(userId);
    try {
      // Same protocol as commerce: dedicated reset-password endpoint that
      // generates a new random password, hashes it, and emails it. The new
      // password is NEVER returned in the response — only in the email.
      const result = await api.resetCivilRespondentUserPassword(managingCivilOrg.id, userId);
      if (result?.emailSent) {
        setSuccess(t('network.passwordResetSuccess') || 'Password reset. New password emailed.');
      } else {
        setSuccess(t('network.passwordResetNoEmail') || 'Password reset, but the email could not be sent. Contact the user out-of-band.');
      }
      setTimeout(() => setSuccess(''), 4000);
    } catch (err: any) {
      alert(t('network.passwordResetFailed') || 'Failed: ' + (err.message || 'unknown'));
    } finally {
      setResettingPasswordUserId(null);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!managingCivilOrg) return;
    if (!confirm(t('network.confirmDeleteUser') || 'Delete this user?')) return;
    try {
      await api.deleteCivilRespondentUser(managingCivilOrg.id, userId);
      const users = await api.listCivilRespondentUsers(managingCivilOrg.id);
      setCivilOrgUsers(Array.isArray(users) ? users : []);
    } catch (err: any) {
      alert('Failed: ' + (err.message || 'unknown'));
    }
  };

  const handleAddOrgUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addUserToOrg.orgId) return;
    if (!newOrgUserEmail || !newOrgUserName) {
      setNewOrgUserError(t('network.userEmailNameRequired') || 'Email and name are required');
      return;
    }
    setNewOrgUserError('');
    try {
      await api.createCivilRespondentUser(addUserToOrg.orgId, {
        email: newOrgUserEmail,
        name: newOrgUserName,
        role: newOrgUserRole,
      });
      // Reset and refresh
      setNewOrgUserEmail('');
      setNewOrgUserName('');
      setNewOrgUserRole('agent');
      setAddUserToOrg({ orgId: '', show: false });
      const users = await api.listCivilRespondentUsers(addUserToOrg.orgId);
      setCivilOrgUsers(Array.isArray(users) ? users : []);
      setSuccess(t('network.userCreatedWithEmail') || 'User created. Welcome email sent.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setNewOrgUserError(err.message || 'Failed');
    }
  };

const handleAddStore = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const admins = [];
    if (admin1Email && admin1Password) admins.push({ email: admin1Email, password: admin1Password });
    if (admin2Email && admin2Password) admins.push({ email: admin2Email, password: admin2Password });
    try {
      await api.createStore({ ...newStore, admins: admins.length > 0 ? admins : undefined });
      setShowAddModal(false);
      // Reset form including new admin fields
      setAdmin1Email(''); setAdmin1Password('');
      setAdmin2Email(''); setAdmin2Password('');
      setNewStore({ name: '', country_code: '', city_code: '', admin_email: '', client_id: '', guardian_enabled: true, access_control_enabled: false, network_enabled: false, max_guards: 4, max_admins: 2, store_number: null, hostname: '' });
      fetchStores();
    } catch (err: any) {
      setError(err.message || 'Failed to create store');
    }
  };

  // Simple toggle component (you can replace with shadcn Switch)
  const FeatureToggle = ({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) => (
    <div className="flex items-center justify-between gap-4 py-1.5">
      <span className="text-sm">{label}</span>
      <button
        type="button"
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? 'bg-primary' : 'bg-accent'}`}
        onClick={() => onChange(!checked)}
      >
        <span className={`inline-block h-4 w-4 transform rounded-full bg-card transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
      </button>
    </div>
  );

  // ----- Render -----
  return (

    // Main Form
    <div className="min-h-screen bg-background">
      <Header view="admin" />
      <TrainingSimulation availableStores={stores} showAlways />

      {/* Add Clients */}
      <div className="max-w-7xl mx-auto p-3 sm:p-6">
        <div className="mb-4">
          <h1 className="text-2xl font-bold">{t('network.title')}</h1>
        </div>

        {/* Tab bar — Commerce vs Civil Respondents vs OPSEC */}
        <div className="border-b border-border mb-6 flex flex-wrap gap-1" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'commerce'}
            onClick={() => setActiveTab('commerce')}
            className={`flex-1 sm:flex-none min-w-[45%] sm:min-w-0 px-3 sm:px-4 py-2 -mb-px border-b-2 text-xs sm:text-sm font-medium transition-colors ${
              activeTab === 'commerce'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Building className="w-4 h-4 inline mr-2" />
            {t('network.tabCommerce') || 'Commerce'}
          </button>
          {/* v0.4.9.2.4c11: hide Civil Respondents for a TAdmin. The
              OPSEC tab below is hidden on the same reasoning — a TAdmin
              provisions Edge Servers and installs Cameras, and has no
              business in law-enforcement / emergency-services org data. */}
          {!isTadmin && (
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'civil'}
            onClick={() => setActiveTab('civil')}
            className={`flex-1 sm:flex-none min-w-[45%] sm:min-w-0 px-3 sm:px-4 py-2 -mb-px border-b-2 text-xs sm:text-sm font-medium transition-colors ${
              activeTab === 'civil'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Shield className="w-4 h-4 inline mr-2" />
            {t('network.tabCivilRespondents') || 'Civil Respondents'}
          </button>
          )}
          {/* v0.4.9.2.4c5: TAdmins cannot view the OPSEC dashboard.
              The backend's requireOpsAdmin middleware is the real gate;
              this hides the tab so TAdmins never see a control that
              will bounce. */}
          {!isTadmin && (
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'opsec'}
            onClick={() => setActiveTab('opsec')}
            className={`flex-1 sm:flex-none min-w-[45%] sm:min-w-0 px-3 sm:px-4 py-2 -mb-px border-b-2 text-xs sm:text-sm font-medium transition-colors ${
              activeTab === 'opsec'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <ShieldCheck className="w-4 h-4 inline mr-2" />
            {t('network.tabOpsec') || 'OPSEC'}
          </button>
          )}
          {/* v0.4.9.4.6: AI Training. superadmin AND sadmin — the brief is
              explicit that an SAdmin must be able to train the AI, download
              the training data and read the LLM prompt. That matches
              requireAiTraining in server/routes/lpr.js, which is the real
              gate; this hides the tab so a TAdmin, cadmin or guard never
              sees a control that will bounce.
              Deliberately NOT `!isTadmin`: that would also admit cadmin and
              guard, who are B2B client roles. */}
          {(isSuperadmin || isSadmin) && (
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'aiTraining'}
            onClick={() => setActiveTab('aiTraining')}
            className={`flex-1 sm:flex-none min-w-[45%] sm:min-w-0 px-3 sm:px-4 py-2 -mb-px border-b-2 text-xs sm:text-sm font-medium transition-colors ${
              activeTab === 'aiTraining'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Cpu className="w-4 h-4 inline mr-2" />
            {t('network.tabAiTraining') || 'AI Training'}
          </button>
          )}
        </div>

        {success && <div className="mb-4 p-2 bg-primary/10 text-primary rounded">{success}</div>}
        {error && <div className="mb-4 p-2 bg-destructive/10 text-destructive rounded">{error}</div>}

        {/* ---- Commerce tab ---- */}
        {activeTab === 'commerce' && (
          <>
            <div className="flex flex-wrap gap-2 mb-6">
              <Button variant="outline" onClick={() => setShowAddClientModal(true)} disabled={user?.role !== 'superadmin' && user?.role !== 'sadmin'}>
                <Building className="w-4 h-4 mr-2" /> {t('network.addClient')}
              </Button>
              {/* v0.4.9.2.4c5: bulk user assignment. One person = one
                  account; these add them to more stores instead of
                  minting a duplicate per store. Employee-only — the
                  elevated roles. SAdmin gets the same access as SuperAdmin
                  for tadmin/cadmin/admin/agent — enforced server-side in
                  POST /api/stores/:id/users, which allows superadmin OR
                  sadmin. Assigning the *sadmin* or *superadmin* global role
                  is still superadmin-only, and the SAdmin-created SAdmin
                  case is rejected by the server. */}
              {(isSuperadmin || isSadmin) && (
                <>
                  <span className="self-center text-xs text-muted-foreground px-1">|</span>
                  {([
                    ['tadmin', 'TAdmin', 'Technical Admin'],
                    ['sadmin', 'SAdmin', 'Support Admin'],
                    ['admin', 'Admin', 'Store Admin'],
                    ['guard', 'Guard', 'Guard'],
                  ] as const).map(([role, label]) => (
                    <Button
                      key={`bulk-${role}`}
                      variant="outline"
                      onClick={() => { setBulkRole(role); setShowBulkAssign(true); }}
                      title={`Add an existing ${label} to one or more stores`}
                    >
                      <Plus className="w-4 h-4 mr-2" /> {label}
                    </Button>
                  ))}
                </>
              )}
              {/* v0.4.9.2.4c5: TAdmins are read-only on store CRUD —
                  they provision Edge Servers and install Cameras in
                  existing stores, but cannot create them. The API gate
                  is routes/stores.js superadminOnly; this hides the
                  affordance so the UI matches the rule. */}
              {!isTadmin && (
                <Button onClick={() => setShowAddModal(true)}>
                  <Plus className="w-4 h-4 mr-2" /> {t('network.addStore')}
                </Button>
              )}
            </div>

            {loading ? (
              <p>{t('common.loading')}</p>
            ) : (
              <div className="space-y-6">
                {clientEntries.map((entry) => (
              <Card key={entry.clientId} className="p-0.2">
              <div className="flex items-center justify-between cursor-pointer"
                  onClick={() => setExpandedClientId(expandedClientId === entry.clientId ? null : entry.clientId)}>
                <div className="flex items-center gap-3">
                  {expandedClientId === entry.clientId ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  <h2 className="text-lg font-semibold">{entry.name}</h2>
                  <span className="text-sm text-muted-foreground">({entry.stores.length} store{entry.stores.length !== 1 ? 's' : ''})</span>
                </div>
                {/* Training Mode toggle — superadmin/sadmin only.
                    v0.4.9.2.4c12: 'isTadmin' added. The toggle flips
                    clients.training_mode_enabled, which is client
                    management data — outside the TAdmin's
                    provisioning/Camera-install scope. The 'Training Mode
                    label is always rendered; only the switch control is
                    hidden, so the card layout does not shift. */}
                {!isTadmin && (
                <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Training</span>
                  <button
                    type="button"
                    disabled={updatingClientId === entry.clientId}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      entry.training_mode_enabled ? 'bg-primary' : 'bg-accent'
                    }`}
                    onClick={async (e) => {
                      e.stopPropagation();
                      const newValue = !entry.training_mode_enabled;
                      setUpdatingClientId(entry.clientId);
                      try {
                        await api.updateClient(entry.clientId, { training_mode_enabled: newValue });
                        // Update local clients state
                        setClients(prev =>
                          prev.map(c =>
                            c.id === entry.clientId ? { ...c, training_mode_enabled: newValue } : c
                          )
                        );
                        setSuccess('Client updated');
                        setTimeout(() => setSuccess(''), 2000);
                      } catch (err: any) {
                        setError(err.message || 'Update failed');
                      } finally {
                        setUpdatingClientId(null);
                      }
                    }}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-card transition-transform ${
                      entry.training_mode_enabled ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                )}
              </div>

                {/*  STORES SETTINGS ADD/DELETE/EDIT */}
                {expandedClientId === entry.clientId && (
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {entry.stores.length === 0 && (
                      <p className="text-sm text-muted-foreground col-span-full">No stores yet.</p>
                      )}
                    {entry.stores.map(store => (
                      <Card key={store.id} className="p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold flex items-center gap-2">
                              <Store className="w-4 h-4 text-primary" /> {store.name}
                            </h3>
                            <p className="text-sm font-mono text-muted-foreground">{store.code}</p>
                          </div>
                          <Button size="sm" variant="outline" onClick={(e) => {
                            e.stopPropagation();
                            const fullStore = { ...store, network_countries: store.network_countries || [] };
                            setSelectedStore(fullStore);
                            setProvisioningInfo(null);
                            // setSelectedStore(store);
                            fetchStoreUsers(store.id);
                            api.getStorePois(store.id).then(data => {
                              setStorePoiCodes(data.map((p: any) => p.code));
                            }).catch(err => {
                              console.error(err);
                              setStorePoiCodes([]); // fallback
                            });
                          }}>
                            <Settings className="w-3 h-3 mr-1" /> Manage
                          </Button>
                        </div>
                        <div className="mt-3 space-y-1 text-sm">
                          <div className="flex items-center gap-2">
                            <Shield className="w-4 h-4 text-blue-500" />
                            <FeatureToggle label="Guardian" checked={store.guardian_enabled} onChange={(v) => handleToggleFeature(store.id, 'guardian_enabled', v)} />
                          </div>
                          <div className="flex items-center gap-2">
                            <Car className="w-4 h-4 text-primary" />
                            <FeatureToggle label="Access Control" checked={store.access_control_enabled} onChange={(v) => handleToggleFeature(store.id, 'access_control_enabled', v)} />
                          </div>
                          <div className="flex items-center gap-2">
                            <Network className="w-4 h-4 text-purple-500" />
                            <FeatureToggle label="Network" checked={store.network_enabled} onChange={(v) => handleToggleFeature(store.id, 'network_enabled', v)} />
                          </div>
                          <h3 className="font-semibold text-lg mt-4">Network Countries</h3>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {allCountries.map(country => {
                              const currentCountryList = store.network_countries || [];
                              const selected = currentCountryList.includes(country);
                              return (
                                <button
                                  key={country}
                                  className={`px-3 py-1 rounded-full text-sm border ${
                                    selected ? 'bg-primary text-primary-foreground border-primary' : 'bg-background'
                                  }`}
                                  onClick={async (e) => {
                                    e.stopPropagation(); // prevent card click from toggling collapse
                                    const updated = selected
                                      ? currentCountryList.filter(c => c !== country)
                                      : [...currentCountryList, country];
                                    try {
                                      await api.updateStoreNetworkCountries(store.id, updated);
                                      // Update the stores list with new network_countries for this store
                                      setStores(prev =>
                                        prev.map(s =>
                                          s.id === store.id ? { ...s, network_countries: updated } : s
                                        )
                                      );
                                    } catch (err) {
                                      console.error(err);
                                    }
                                  }}
                                >
                                  {country} {selected ? '✓' : '+'}
                                </button>
                              );
                            })}
                          </div>
                          {/* v0.4.9.2.4c12: hidden for TAdmin. Max
                              Guards/Admins is a commercial setting
                              (per-store seat limits), not provisioning.
                              The label is still rendered so a TAdmin
                              can see the configured values; only the
                              <input> edits are gone. */}
                          {!isTadmin && (
                          <div className="flex items-center gap-2 mt-3">
                            <Users className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm">Max Guards:</span>
                            <input
                              type="number"
                              min={1}
                              max={100}
                              value={localMax[store.id]?.max_guards ?? store.max_guards}
                              onChange={(e) => {
                                const val = parseInt(e.target.value, 10);
                                if (!isNaN(val)) {
                                  setLocalMax(prev => ({
                                    ...prev,
                                    [store.id]: { ...prev[store.id], max_guards: val },
                                  }));
                                }
                              }}
                              onBlur={() => {
                                const current = localMax[store.id]?.max_guards ?? store.max_guards;
                                if (current !== store.max_guards) {
                                  handleToggleFeature(store.id, 'max_guards', current);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  const current = localMax[store.id]?.max_guards ?? store.max_guards;
                                  if (current !== store.max_guards) {
                                    handleToggleFeature(store.id, 'max_guards', current);
                                  }
                                  (e.target as HTMLInputElement).blur();
                                }
                              }}
                              className="w-20 p-1 border rounded text-sm"
                              style={{ minWidth: '4rem' }}
                            />
                          </div>
                          )}

                          {!isTadmin && (
                          <div className="flex items-center gap-2 mt-1">
                            <Users className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm">Admins:</span>
                            <input
                              type="number"
                              min={1}
                              max={100}
                              value={localMax[store.id]?.max_admins ?? store.max_admins ?? 2}
                              onChange={(e) => {
                                const val = parseInt(e.target.value, 10);
                                if (!isNaN(val)) {
                                  setLocalMax(prev => ({
                                    ...prev,
                                    [store.id]: { ...prev[store.id], max_admins: val },
                                  }));
                                }
                              }}
                              onBlur={() => {
                                const current = localMax[store.id]?.max_admins ?? store.max_admins ?? 2;
                                if (current !== (store.max_admins ?? 2)) {
                                  handleToggleFeature(store.id, 'max_admins', current);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  const current = localMax[store.id]?.max_admins ?? store.max_admins ?? 2;
                                  if (current !== (store.max_admins ?? 2)) {
                                    handleToggleFeature(store.id, 'max_admins', current);
                                  }
                                  (e.target as HTMLInputElement).blur();
                                }
                              }}
                              className="w-20 p-1 border rounded text-sm"
                              style={{ minWidth: '4rem' }}
                            />

                          </div>
                          )}
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
          </>
        )}

        {/* ---- Civil Respondents tab ---- */}
        {activeTab === 'civil' && !isTadmin && (
          <>
            <div className="flex flex-wrap gap-2 mb-6">
              <Button
                onClick={() => setShowAddCivilOrgModal(true)}
                disabled={user?.role !== 'superadmin' && user?.role !== 'sadmin'}
              >
                <Plus className="w-4 h-4 mr-2" /> {t('network.addCivilResponder')}
              </Button>
            </div>

            {civilOrgsLoading ? (
              <p className="text-muted-foreground">{t('common.loading')}</p>
            ) : civilOrgs.length === 0 ? (
              <div className="text-muted-foreground text-sm py-8 text-center">
                {t('network.noCivilOrgs') || 'No civil respondent organizations yet. Click "Add Civil Responder" to create one.'}
              </div>
            ) : (
              <div className="space-y-3">
                {civilOrgs.map((org) => (
                  <Card key={org.id} className="p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4 text-primary" />
                          <h3 className="font-semibold text-foreground">{org.division_name}</h3>
                          <span className="text-xs px-2 py-0.5 rounded bg-accent text-foreground">
                            {org.type === 'law_enforcement' ? t('civil.common.lawEnforcement') : t('civil.common.emergencyServices')}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {/* v0.4.9.2.4c2: `country` now stores alpha-3
                              (e.g. "ESP"), so map it back to the readable
                              name. countryLabel() falls back to the raw
                              value for legacy rows. */}
                          {countryLabel(org.country_code || org.country)}
                          {org.city ? ` · ${org.city}` : ''}
                          {org.phone ? ` · ${org.phone}` : ''}
                          {org.contact_person ? ` · ${org.contact_person}` : ''}
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openManageCivilOrg(org)}
                      >
                        <Settings className="w-4 h-4 mr-2" /> {t('network.manage') || 'Manage'}
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        {/* ---- Add Client Modal ---- */}
        <Dialog open={showAddClientModal} onOpenChange={setShowAddClientModal}>
          <DialogContent>
            <DialogHeader><DialogTitle>Create Client</DialogTitle></DialogHeader>
            {clientModalError && (
              <div className="p-2 mb-2 bg-destructive/10 text-destructive rounded text-sm">{clientModalError}</div>
            )}
            <form onSubmit={handleCreateClient} className="space-y-4" autoComplete="off">
              <div>
                <Label>Company Name *</Label>
                <Input required value={newClientName} onChange={e => setNewClientName(e.target.value)} className="mt-2" />
              </div>
              <div>
                <Label>Address</Label>
                <Input value={newClientAddress} onChange={e => setNewClientAddress(e.target.value)} className="mt-2" />
              </div>
              <div>
                <Label>NIF (Tax ID)</Label>
                <Input 
                  value={newClientNif}
                  readOnly
                  onFocus={(e) => e.target.removeAttribute('readOnly')}
                  onChange={e => setNewClientNif(e.target.value)}
                  placeholder="Enter Tax ID Number"
                  aria-readonly="true"
                  role="textbox"
                />
              </div>
              <div>
                <Label>Cadmin Email</Label>
                <Input
                  type="email"
                  value={newCadminEmail}
                  onChange={e => setNewCadminEmail(e.target.value)}
                  placeholder="cadmin@example.com"
                  className="mt-2"
                  autoComplete="off"
                />
              </div>
              <div>
                <Label>Cadmin Password</Label>
                <Input
                  type="password"
                  value={newCadminPassword}
                  readOnly={true}
                  onFocus={(e) => e.target.removeAttribute('readOnly')}
                  onChange={e => setNewCadminPassword(e.target.value)}
                  placeholder="Min 6 characters"
                  className="mt-2"
                  autoComplete="off"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowAddClientModal(false)}>Cancel</Button>
                <Button type="submit">Create</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* ---- Add Civil Respondent Modal (custom responsive: bottom-sheet on mobile, centered on desktop) ---- */}
        {showAddCivilOrgModal && (
          <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4"
            onClick={() => setShowAddCivilOrgModal(false)}
          >
            <div
              className="bg-card border border-border shadow-xl w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl animate-in fade-in slide-in-from-bottom sm:slide-in-from-bottom-0 sm:zoom-in-95"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-card z-10 flex items-center justify-between p-4 border-b border-border">
                <h2 className="text-base font-semibold text-foreground">{t('network.addCivilResponder')}</h2>
                <button
                  type="button"
                  onClick={() => setShowAddCivilOrgModal(false)}
                  className="text-muted-foreground hover:text-foreground p-1"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleCreateCivilOrg} className="p-4 space-y-4" autoComplete="off">
                {civilOrgModalError && (
                  <div className="p-2 bg-destructive/10 text-destructive rounded text-sm">{civilOrgModalError}</div>
                )}
                <div>
                  <Label>{t('network.civilOrgType')}</Label>
                  <select
                    value={civilOrgType}
                    onChange={e => setCivilOrgType(e.target.value as 'emergency_services' | 'law_enforcement')}
                    className="mt-2 w-full p-2 border border-input rounded bg-background"
                  >
                    <option value="law_enforcement">Law Enforcement</option>
                    <option value="emergency_services">Emergency Services</option>
                  </select>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>{t('network.civilOrgDivision')} *</Label>
                    <Input required value={civilOrgDivision} onChange={e => setCivilOrgDivision(e.target.value)} className="mt-2" />
                  </div>
                  <div>
                    {/* v0.4.9.2.4c2: country picker. Stores alpha-3 in
                        BOTH country and country_code. Replaces the free-text
                        input that let "ES" / "Spain" / "ESP" all be entered. */}
                    <Label>{t('network.civilOrgCountry')} *</Label>
                    <div className="mt-2">
                      <SearchableSelect
                        theme="theme"
                        value={civilOrgPickedCountry ? civilOrgCountry : ''}
                        displayFallback={countryLabel(civilOrgCountry)}
                        onChange={handleAddOrgCountryPick}
                        options={countryListToOptions(COUNTRIES)}
                        placeholder="Search country…"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    {/* v0.4.9.2.4c2: city picker, cascaded off the country.
                        Stores the subdivision NAME in `city` and the full
                        ISO-3166-2 code in `city_code`. */}
                    <Label>{t('network.civilOrgCity')}</Label>
                    <div className="mt-2">
                      <SearchableSelect
                        theme="theme"
                        value={civilOrgCityCode}
                        displayFallback={civilOrgCity}
                        onChange={handleAddOrgCityPick}
                        options={civilOrgSubs.map(s => ({ value: s.code, label: s.name, hint: s.code }))}
                        placeholder={civilOrgPickedCountry && civilOrgCountry ? 'Search city…' : 'Pick a country first'}
                        loading={civilOrgSubsLoading}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>{t('network.civilOrgPhone')}</Label>
                    <Input value={civilOrgPhone} onChange={e => setCivilOrgPhone(e.target.value)} className="mt-2" />
                  </div>
                  <div className="md:col-span-2">
                    <Label>{t('network.civilOrgAddress')}</Label>
                    <Input value={civilOrgAddress} onChange={e => setCivilOrgAddress(e.target.value)} className="mt-2" />
                  </div>
                  <div className="md:col-span-2">
                    <Label>{t('network.civilOrgContact')}</Label>
                    <Input value={civilOrgContact} onChange={e => setCivilOrgContact(e.target.value)} className="mt-2" />
                  </div>
                  <div className="md:col-span-2">
                    <Label>{t('network.civilOrgMaxLicenses') || 'Max user licenses'}</Label>
                    <Input
                      type="number"
                      min={1}
                      max={10000}
                      value={civilOrgMaxLicenses}
                      onChange={e => setCivilOrgMaxLicenses(Math.max(1, parseInt(e.target.value, 10) || 50))}
                      className="mt-2"
                    />
                    <p className="text-xs text-muted-foreground mt-1">{t('network.civilOrgMaxLicensesHint') || 'Default 50. The agentAdmin does not count against this limit.'}</p>
                  </div>
                </div>

                <h3 className="text-sm font-semibold mt-4 mb-2">{t('network.civilFirstUser')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>{t('network.civilUserEmail')} *</Label>
                    <Input type="email" required value={civilAgentAdminEmail} onChange={e => setCivilAgentAdminEmail(e.target.value)} className="mt-2" />
                  </div>
                  <div>
                    <Label>{t('network.civilUserName')} *</Label>
                    <Input required value={civilAgentAdminName} onChange={e => setCivilAgentAdminName(e.target.value)} className="mt-2" />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('network.userCreatedWithEmail')}
                </p>

                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowAddCivilOrgModal(false)}>{t('common.cancel')}</Button>
                  <Button type="submit" disabled={civilOrgSubmitting}>
                    {civilOrgSubmitting ? t('common.saving') : t('common.add')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ---- Manage Civil Respondent Modal ---- */}
        {managingCivilOrg && (
          <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4"
            onClick={closeManageCivilOrg}
          >
            <div
              className="bg-card border border-border shadow-xl w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl animate-in fade-in slide-in-from-bottom sm:slide-in-from-bottom-0 sm:zoom-in-95"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-card z-10 flex items-center justify-between p-4 border-b border-border">
                <div>
                  <h2 className="text-base font-semibold text-foreground">{managingCivilOrg.division_name}</h2>
                  <p className="text-xs text-muted-foreground">
                    {managingCivilOrg.type === 'law_enforcement' ? 'Law Enforcement' : 'Emergency Services'}
                    {managingCivilOrg.country ? ` · ${managingCivilOrg.country}` : ''}
                    {managingCivilOrg.city ? ` · ${managingCivilOrg.city}` : ''}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeManageCivilOrg}
                  className="text-muted-foreground hover:text-foreground p-1"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4 space-y-4">
                {/* ---- Organization info (editable) ---- */}
                <form onSubmit={handleUpdateCivilOrg} className="space-y-3">
                  <h3 className="text-sm font-semibold text-foreground">{t('network.organizationInfo') || 'Organization'}</h3>
                  {editOrgError && (
                    <div className="p-2 bg-destructive/10 text-destructive rounded text-sm">{editOrgError}</div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">{t('network.civilOrgType')}</Label>
                      <select
                        value={editOrgForm.type}
                        onChange={e => setEditOrgForm({ ...editOrgForm, type: e.target.value as 'law_enforcement' | 'emergency_services' })}
                        className="mt-1 w-full p-2 border border-input rounded bg-background"
                      >
                        <option value="law_enforcement">Law Enforcement</option>
                        <option value="emergency_services">Emergency Services</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs">{t('network.civilOrgDivision')} *</Label>
                      <Input required value={editOrgForm.division_name} onChange={e => setEditOrgForm({ ...editOrgForm, division_name: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                      {/* v0.4.9.2.4c2: country picker. Stores alpha-3 in BOTH
                          `country` and `country_code`. displayFallback shows the
                          resolved name for legacy rows that hold "ES" / "Spain". */}
                      <Label className="text-xs">{t('network.civilOrgCountry')} *</Label>
                      <div className="mt-1">
                        <SearchableSelect
                          theme="theme"
                          value={editOrgCodeUserEdited.current ? (editOrgForm.country_code || editOrgForm.country) : ''}
                          displayFallback={countryLabel(editOrgForm.country_code || editOrgForm.country)}
                          onChange={handleEditOrgCountryPick}
                          options={countryListToOptions(COUNTRIES)}
                          placeholder="Search country…"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      {/* v0.4.9.2.4c2: city picker, cascaded off the country.
                          Stores the NAME in `city`, the ISO-3166-2 code in `city_code`. */}
                      <Label className="text-xs">{t('network.civilOrgCity')}</Label>
                      <div className="mt-1">
                        <SearchableSelect
                          theme="theme"
                          value={editOrgForm.city_code}
                          displayFallback={editOrgForm.city}
                          onChange={handleEditOrgCityPick}
                          options={editOrgSubs.map(s => ({ value: s.code, label: s.name, hint: s.code }))}
                          placeholder={editOrgForm.country ? 'Search city…' : 'Pick a country first'}
                          loading={editOrgSubsLoading}
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">{t('network.civilOrgPhone')}</Label>
                      <Input value={editOrgForm.phone} onChange={e => setEditOrgForm({ ...editOrgForm, phone: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs">{t('network.civilOrgContact')}</Label>
                      <Input value={editOrgForm.contact_person} onChange={e => setEditOrgForm({ ...editOrgForm, contact_person: e.target.value })} className="mt-1" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-xs">{t('network.civilOrgAddress')}</Label>
                      <Input value={editOrgForm.address} onChange={e => setEditOrgForm({ ...editOrgForm, address: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs">{t('network.civilOrgMaxLicenses') || 'Max user licenses'}</Label>
                      <Input
                        type="number"
                        min={1}
                        max={10000}
                        value={editOrgForm.max_licenses}
                        onChange={e => setEditOrgForm({ ...editOrgForm, max_licenses: Math.max(1, parseInt(e.target.value, 10) || 50) })}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button type="submit" disabled={editOrgSubmitting}>
                      {editOrgSubmitting ? t('common.saving') : t('common.save')}
                    </Button>
                  </div>
                </form>

                <div className="border-t border-border" />

                <div>
                  {/* Users header with the "X/Y" count on the right.
                      IMPORTANT: the agentAdmin does NOT count against the
                      license limit. So X = total users minus the number
                      of agentAdmin users in this org (typically 1). The
                      superadmin can raise Y at any time via the form
                      above. The "Add user" button is disabled when X
                      reaches Y, with a tooltip explaining why. */}
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-foreground">{t('network.civilUsers') || 'Users'}</h3>
                    <span className={`text-xs font-medium ${
                      (() => {
                        const max = managingCivilOrg?.max_licenses ?? 50;
                        const used = civilOrgUsers.filter(u => u.role !== 'agentAdmin').length;
                        return used >= max ? 'text-destructive' : used / max >= 0.8 ? 'text-amber-600' : 'text-muted-foreground';
                      })()
                    }`}>
                      {civilOrgUsers.filter(u => u.role !== 'agentAdmin').length}
                      {' / '}
                      {managingCivilOrg?.max_licenses ?? 50}
                    </span>
                  </div>
                  {civilOrgUsersLoading ? (
                    <div className="text-sm text-muted-foreground py-4 text-center">{t('common.loading')}</div>
                  ) : civilOrgUsers.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-4 text-center">{t('network.noUsersYet') || 'No users yet.'}</div>
                  ) : (
                    <div className="space-y-2">
                      {civilOrgUsers.map((u) => (
                        <div key={u.id} className="flex items-center justify-between p-3 bg-background border border-border rounded-lg">
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-foreground truncate">{u.name || u.email}</div>
                            <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                              <span className="inline-block px-1.5 py-0.5 rounded bg-accent text-foreground">
                                {u.role === 'agentAdmin' ? 'Agent Admin' : 'Agent'}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 ml-2">
                            <button
                              type="button"
                              onClick={() => setResetTarget({
                                userId: u.id,
                                userName: u.name || u.email,
                                userEmail: u.email,
                              })}
                              disabled={resettingPasswordUserId === u.id}
                              className="p-2 text-muted-foreground hover:bg-accent rounded"
                              title={t('network.resetPassword') || 'Reset password'}
                            >
                              <KeyRound className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteUser(u.id)}
                              className="p-2 text-red-500 hover:bg-destructive/10 rounded"
                              title={t('common.delete')}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Add user form */}
                {addUserToOrg.show && addUserToOrg.orgId === managingCivilOrg.id ? (
                  <form onSubmit={handleAddOrgUser} className="p-3 bg-accent border border-border rounded-lg space-y-3">
                    <h4 className="text-sm font-semibold text-foreground">{t('network.addUser') || 'Add user'}</h4>
                    {newOrgUserError && (
                      <div className="p-2 bg-destructive/10 text-destructive rounded text-sm">{newOrgUserError}</div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">{t('network.civilUserEmail')} *</Label>
                        <Input type="email" required value={newOrgUserEmail} onChange={e => setNewOrgUserEmail(e.target.value)} className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs">{t('network.civilUserName')} *</Label>
                        <Input required value={newOrgUserName} onChange={e => setNewOrgUserName(e.target.value)} className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs">Role</Label>
                        <select
                          value={newOrgUserRole}
                          onChange={e => setNewOrgUserRole(e.target.value as 'agent' | 'agentAdmin')}
                          className="mt-1 w-full p-2 border border-input rounded bg-background"
                        >
                          <option value="agent">Agent</option>
                          <option value="agentAdmin">Agent Admin</option>
                        </select>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">{t('network.userCreatedWithEmail')}</p>
                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" onClick={() => setAddUserToOrg({ orgId: '', show: false })}>{t('common.cancel')}</Button>
                      <Button type="submit">{t('common.add')}</Button>
                    </div>
                  </form>
                ) : (
                  (() => {
                    const max = managingCivilOrg?.max_licenses ?? 50;
                    const used = civilOrgUsers.filter(u => u.role !== 'agentAdmin').length;
                    const atLimit = used >= max;
                    return (
                      <Button
                        variant="outline"
                        onClick={() => setAddUserToOrg({ orgId: managingCivilOrg.id, show: true })}
                        className="w-full"
                        disabled={atLimit}
                        title={atLimit
                          ? (t('network.licensesReached') || `License limit reached (${used}/${max}). Ask a superadmin to increase the allowance.`)
                          : undefined}
                      >
                        <Plus className="w-4 h-4 mr-2" /> {t('network.addUser') || 'Add user'}
                      </Button>
                    );
                  })()
                )}
              </div>
            </div>
          </div>
        )}

        {/* v0.4.9.2.4c4: bulk user assignment — shared component. */}
        {showBulkAssign && (
          <UserAddToStoresModal
            stores={stores.map((st) => ({ id: st.id, name: st.name }))}
            role={bulkRole === 'guard' ? 'guard' : 'admin'}
            globalRole={bulkRole}
            mode="add"
            onClose={() => setShowBulkAssign(false)}
            onSuccess={() => { setShowBulkAssign(false); fetchStores(); }}
          />
        )}

        {/* Add Store Modal */}
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogContent className="w-[calc(100vw-2rem)] max-w-lg sm:max-w-2xl lg:max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('network.addStore')}</DialogTitle>
            </DialogHeader>
            {modalError && (
              <div className="p-2 mb-2 bg-destructive/10 text-destructive rounded text-sm">{modalError}</div>
            )}
            <form onSubmit={handleAddStore} className="space-y-4">
            <div>
                <Label>Client (required)</Label>
                <select
                  className="w-full mt-2 p-2 border rounded bg-background"
                  value={newStore.client_id}
                  onChange={e => setNewStore({...newStore, client_id: e.target.value})}
                  required
                >
                  <option value="" disabled>Select a client</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Store Name</Label>
                <Input className="mt-2" required value={newStore.name} onChange={e => setNewStore({...newStore, name: e.target.value})} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <Label>Country Code</Label>
                  <Input className="mt-2" required maxLength={3} placeholder="ES" value={newStore.country_code} onChange={e => setNewStore({...newStore, country_code: e.target.value.toUpperCase()})} />
                </div>
                <div>
                  <Label>City Code</Label>
                  <Input className="mt-2" required maxLength={3} placeholder="MAD" value={newStore.city_code} onChange={e => setNewStore({...newStore, city_code: e.target.value.toUpperCase()})} />
                </div>
              </div>
              {/* v0.4.9.1.12: Store number + hostname preview */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <Label>Store Number (auto)</Label>
                  <Input
                    className="mt-2"
                    type="number"
                    min={1}
                    max={999999}
                    placeholder="000001"
                    value={newStore.store_number ?? ''}
                    onChange={e => setNewStore({
                      ...newStore,
                      store_number: e.target.value === '' ? null : parseInt(e.target.value, 10)
                    })}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Next available: {proposedHostname.split('-').pop()?.replace('.isentinel360', '') || '—'}
                  </p>
                </div>
                <div>
                  <Label>Hostname (auto)</Label>
                  <Input
                    className="mt-2"
                    value={newStore.hostname || proposedHostname}
                    readOnly={true}
                    placeholder="es-mad-000001.isentinel360"
                    onFocus={e => e.target.removeAttribute('readOnly')}
                  />
                  <p className={`text-xs mt-1 ${hostnameAvailable ? 'text-muted-foreground' : 'text-destructive'}`}>
                    {checkingHostname ? 'Checking…' : (hostnameAvailable ? 'Available ✓' : 'Already taken — pick another number')}
                  </p>
                </div>
              </div>
              <div>
                <Label>Admin Email (must exist)</Label>
                <Input 
                  className="mt-2" 
                  type="email" 
                  required value={newStore.admin_email} 
                  readOnly={true}
                  onFocus={(e) => e.target.removeAttribute('readOnly')}
                  onChange={e => setNewStore({...newStore, admin_email: e.target.value})} />
              </div>
              {/* Additional Admin 1 */}
              <div>
                <Label>Additional Admin 1 (optional)</Label>
                <Input
                  className="mt-2"
                  type="email"
                  placeholder="admin1@example.com"
                  value={admin1Email}
                  onChange={e => setAdmin1Email(e.target.value)}
                />
                <Input
                  className="mt-2"
                  type="password"
                  placeholder="Password for admin 1"
                  value={admin1Password}
                  onChange={e => setAdmin1Password(e.target.value)}
                />
              </div>
              {/* Additional Admin 2 */}
              <div>
                <Label>Additional Admin 2 (optional)</Label>
                <Input
                  className="mt-2"
                  type="email"
                  placeholder="admin2@example.com"
                  value={admin2Email}
                  onChange={e => setAdmin2Email(e.target.value)}
                />
                <Input
                  className="mt-2"
                  type="password"
                  placeholder="Password for admin 2"
                  value={admin2Password}
                  onChange={e => setAdmin2Password(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Product Features</Label>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={newStore.guardian_enabled} onChange={e => setNewStore({...newStore, guardian_enabled: e.target.checked})} />
                  <span className="text-sm">Guardian</span>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={newStore.access_control_enabled} onChange={e => setNewStore({...newStore, access_control_enabled: e.target.checked})} />
                  <span className="text-sm">Access Control</span>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={newStore.network_enabled} onChange={e => setNewStore({...newStore, network_enabled: e.target.checked})} />
                  <span className="text-sm">Network (Premium)</span>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm">Max Guards</Label>
                  <Input
                    type="number"
                    min={1} max={100}
                    value={newStore.max_guards}
                    onChange={e => setNewStore({...newStore, max_guards: parseInt(e.target.value) || 4})}
                    className="w-20"
                  />
                </div>

              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
                <Button type="submit">Create</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Store Detail & User Management Modal */}
        <Dialog open={!!selectedStore} onOpenChange={(open) => { if (!open) { setSelectedStore(null); setProvisioningInfo(null); } }}>
          <DialogContent className="w-[calc(100vw-2rem)] max-w-lg sm:max-w-2xl lg:max-w-3xl max-h-[90vh] overflow-y-auto">
            {selectedStore && (
              <>
                <DialogHeader>
                  <DialogTitle className="pr-8 truncate">{selectedStore.name} – Management</DialogTitle>
                </DialogHeader>

                {/* v0.4.9.2.4c5 (TAdmin) — tab bar. TAdmins get Provisioning
                    only; the Users/POIs tab is not rendered for them. */}
                <div className="flex gap-1 border-b mb-4" role="tablist">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={storeTab === 'provisioning'}
                    onClick={() => setStoreTab('provisioning')}
                    className={`flex-1 sm:flex-none min-w-[45%] sm:min-w-0 px-3 sm:px-4 py-2 -mb-px border-b-2 text-xs sm:text-sm font-medium transition-colors ${
                      storeTab === 'provisioning'
                        ? 'border-primary text-primary'
                        : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Provisioning
                  </button>
                  {canSeeStoreData && (
                    <button
                      type="button"
                      role="tab"
                      aria-selected={storeTab === 'data'}
                      onClick={() => setStoreTab('data')}
                      className={`flex-1 sm:flex-none min-w-[45%] sm:min-w-0 px-3 sm:px-4 py-2 -mb-px border-b-2 text-xs sm:text-sm font-medium transition-colors ${
                        storeTab === 'data'
                          ? 'border-primary text-primary'
                          : 'border-transparent text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      Users/POIs
                    </button>
                  )}
                </div>

                {storeTab === 'provisioning' && (<div className="contents">
                {/* Edge Configuration */}
                <div className="space-y-4 border-t pt-4">
                  <h3 className="font-semibold text-lg">Edge Device (Local Network)</h3>
                  <div className="grid gap-3">
                    <div>
                      <Label className="text-sm">Edge Server IP</Label>
                      <Input
                        value={selectedStore.edge_server_ip || ''}
                        onChange={async (e) => {
                          const newIp = e.target.value;
                          try {
                            await api.updateStore(selectedStore.id, { edge_server_ip: newIp });
                            // Update local store object
                            setSelectedStore({ ...selectedStore, edge_server_ip: newIp });
                            // Also refresh the stores list
                            fetchStores();
                          } catch (err: any) {
                            setError(err.message);
                          }
                        }}
                        placeholder="e.g., 192.168.1.100"
                      />
                      <p className="text-xs text-muted-foreground mt-1">IP address of the Jetson Orin on the local network.</p>
                    </div>
                    <div>
                      <Label className="text-sm">Edge Server Port</Label>
                      <Input
                        type="number"
                        value={selectedStore.edge_server_port ?? 3002}
                        onChange={async (e) => {
                          const newPort = parseInt(e.target.value, 10);
                          if (isNaN(newPort)) return;
                          try {
                            await api.updateStore(selectedStore.id, { edge_server_port: newPort });
                            setSelectedStore({ ...selectedStore, edge_server_port: newPort });
                            fetchStores();
                          } catch (err: any) {
                            setError(err.message);
                          }
                        }}
                      />
                    </div>
                    <div>
                      <Label className="text-sm">{t('provisioning.wifiSsid')} <span className="text-red-500">*</span></Label>
                      <Input
                        value={selectedStore.wifi_ssid || ''}
                        onChange={async (e) => {
                          const newSsid = e.target.value;
                          try {
                            await api.updateStore(selectedStore.id, { wifi_ssid: newSsid });
                            setSelectedStore({ ...selectedStore, wifi_ssid: newSsid });
                            fetchStores();
                          } catch (err: any) {
                            setError(err.message);
                          }
                        }}
                        placeholder={t('provisioning.wifiSsidPlaceholder')}
                        required
                      />
                      <p className="text-xs text-muted-foreground mt-1">{t('provisioning.wifiSsidHint')}</p>
                    </div>
                  </div>
                </div>

                {/* v0.4.9.1.12: Provisioning Section */}
                <div className="space-y-4 border-t pt-4">
                  <h3 className="font-semibold text-lg">{t('provisioning.provisioningSection')}</h3>
                  <div className="grid gap-3">
                    <div>
                      <Label className="text-sm">{t('provisioning.hostnameLabel')}</Label>
                      <Input
                        value={selectedStore.hostname || ''}
                        readOnly
                        placeholder={t('provisioning.hostnamePlaceholder')}
                        className="font-mono"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('provisioning.hostnameHint')}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm">{t('provisioning.provisioningStatus')}</Label>
                      <div className="mt-2 flex items-center gap-2">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          selectedStore.provisioning_status === 'provisioned' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                          selectedStore.provisioning_status === 'failed' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                          selectedStore.provisioning_status === 'reset' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' :
                          'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                        }`}>
                          {selectedStore.provisioning_status === 'provisioned' ? t('provisioning.provisioningStatusProvisioned') :
                           selectedStore.provisioning_status === 'failed' ? t('provisioning.provisioningStatusFailed') :
                           selectedStore.provisioning_status === 'reset' ? t('provisioning.provisioningStatusReset') :
                           t('provisioning.provisioningStatusPending')}
                        </span>
                        {selectedStore.edge_provisioned_at && (
                          <span className="text-xs text-muted-foreground">
                            {t('provisioning.sinceLabel', { date: new Date(selectedStore.edge_provisioned_at).toLocaleString() })}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={async () => {
                          try {
                            setError(null);
                            const result = await api.provisionStore(selectedStore.id);
                            setProvisioningInfo({
                              setup_url: result.setup_url,
                              provisioning_token: result.provisioning_token,
                              expires_at: result.expires_at
                            });
                            // v0.4.9.1.12 hotfix v8: poll for status while modal is open.
                            // When the user clicks "Generate Provisioning URL", a setup_url is shown
                            // and they navigate to the Edge to complete provisioning. While they're
                            // gone, the Edge calls /confirm which flips status to 'provisioned'.
                            // We poll every 5s to refresh the local state so when the user comes
                            // back to the PWA, the badge shows green without a manual refresh.
                            if (provisioningPollIntervalRef.current) clearInterval(provisioningPollIntervalRef.current);
                            provisioningPollIntervalRef.current = setInterval(async () => {
                              try {
                                const stores = await api.getStores();
                                const updated = stores.find((s: any) => s.id === selectedStore.id);
                                if (updated) {
                                  setSelectedStore(updated);
                                  if (updated.provisioning_status === 'provisioned') {
                                    // Stop polling once provisioning is confirmed.
                                    if (provisioningPollIntervalRef.current) clearInterval(provisioningPollIntervalRef.current);
                                    provisioningPollIntervalRef.current = null;
                                  }
                                }
                              } catch (err) {
                                // ignore — keep polling
                              }
                            }, 5000);
                            fetchStores();
                          } catch (err: any) {
                            setError(err.message);
                          }
                        }}
                        disabled={!selectedStore.hostname || selectedStore.provisioning_status === 'provisioned'}
                      >
                        {selectedStore.provisioning_status === 'provisioned' ? t('provisioning.alreadyProvisioned') : t('provisioning.generateProvisioningUrl')}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={async () => {
                          if (!confirm(t('provisioning.confirmResetProvisioning'))) return;
                          try {
                            setError(null);
                            await api.resetStoreProvisioning(selectedStore.id);
                            // v0.4.9.1.12 hotfix v7: update local selectedStore state so the
                            // provisioning badge flips back to 'pending' (yellow) and the
                            // Provision button becomes enabled WITHOUT requiring a manual page refresh.
                            // Bug: previously only fetchStores() was called which updated the store
                            // LIST but not the selectedStore object used by this dialog.
                            setSelectedStore({
                              ...selectedStore,
                              provisioning_status: 'reset',
                              edge_provisioned_at: null,
                            });
                            setProvisioningInfo(null);
                            fetchStores();
                          } catch (err: any) {
                            setError(err.message);
                          }
                        }}
                      >
                        {t('provisioning.resetProvisioning')}
                      </Button>
                    </div>

                    {provisioningInfo && provisioningInfo.setup_url && (
                      <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-sm space-y-2">
                        <p className="font-semibold text-blue-900 dark:text-blue-200">{t('provisioning.provisioningUrlGenerated')}</p>
                        <p className="text-xs text-blue-800 dark:text-blue-300">
                          {t('provisioning.provisioningUrlInstructions')}
                        </p>
                        <code className="block text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 p-2 rounded font-mono break-all">
                          {provisioningInfo.setup_url}
                        </code>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              navigator.clipboard.writeText(provisioningInfo.setup_url || '');
                              alert(t('provisioning.copiedToClipboard'));
                            }}
                          >
                            {t('provisioning.copyUrl')}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              navigator.clipboard.writeText(provisioningInfo.provisioning_token || '');
                              alert(t('provisioning.tokenCopied'));
                            }}
                          >
                            {t('provisioning.copyTokenOnly')}
                          </Button>
                        </div>
                        {provisioningInfo.expires_at && (
                          <p className="text-xs text-blue-700 dark:text-blue-400">
                            {t('provisioning.tokenExpires', { date: new Date(provisioningInfo.expires_at).toLocaleString() })}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                </div>)}

                {storeTab === 'data' && canSeeStoreData && (<div className="contents">
                {/* User Management */}
                <div className="space-y-4 border-b pb-4">
                  <h3 className="font-semibold text-lg">Users</h3>
                  <div className="space-y-3">
                    {/* Name Input */}
                    <div>
                      <label className="block text-sm font-medium text-muted-foreground mb-1">
                        Name <span className="text-destructive">*</span> (required)
                    </label>
                    <Input
                      placeholder="Name"
                      value={userName}
                      readOnly={true}
                      onFocus={(e) => e.target.removeAttribute('readOnly')}
                      onChange={(e) => setUserName(e.target.value)}
                      className="w-full"
                    />
                    </div>
                    {/* Email Input */}
                    <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-1">
                      Email
                    </label>
                    <Input
                      placeholder="User email"
                      value={userEmail}
                      readOnly={true}
                      onFocus={(e) => e.target.removeAttribute('readOnly')}
                      onChange={(e) => setUserEmail(e.target.value)}
                      className="w-full"
                    />
                    </div>
                    {/* v0.4.9.2.4c5 — Local Role + Global Role.
                        Local Role (store_users.role_in_store) is the per-store
                        grant: what this user may do IN THIS store.
                        Global Role (users.role) is the account ceiling: the
                        highest tier the user can reach anywhere. Defaults to
                        mirroring the Local Role; an employee may widen it
                        (e.g. to TAdmin) to create the user in one step.
                        The server re-checks the elevated-role gate, so this
                        dropdown cannot grant more than the caller holds. */}
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">Local Role (this store)</label>
                      <select
                        value={userRole}
                        onChange={(e) => setUserRole(e.target.value as 'guard' | 'admin')}
                        className="w-full p-2 border border-input rounded"
                      >
                        <option value="guard">Guard</option>
                        <option value="admin">Store Admin</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">Global Role (account)</label>
                      <select
                        value={userGlobalRole}
                        onChange={(e) => setUserGlobalRole(e.target.value)}
                        className="w-full p-2 border border-input rounded"
                      >
                        <option value="guard">Guard</option>
                        <option value="admin">Admin</option>
                        {/* Employee technical / client-leadership roles:
                            a SAdmin or SuperAdmin may grant these. */}
                        {(isSuperadmin || isSadmin) && (
                          <>
                            <option value="cadmin">Chief Admin</option>
                            <option value="tadmin">TAdmin (Technical Admin)</option>
                          </>
                        )}
                        {/* SAdmin and Super Admin are superadmin-only —
                            same rule the edit form applies, so a SAdmin
                            cannot mint a peer. */}
                        {isSuperadmin && (
                          <>
                            <option value="sadmin">SAdmin</option>
                            <option value="superadmin">Super Admin</option>
                          </>
                        )}
                      </select>
                      <p className="text-[10px] text-muted-foreground mt-1">
                        {isSuperadmin
                          ? 'You can grant any role.'
                          : isSadmin
                          ? 'You can grant Chief Admin and TAdmin. SAdmin and Super Admin are reserved to a SuperAdmin.'
                          : 'You can create Guard or Admin accounts.'}
                      </p>
                    </div>
                    <Button onClick={() => handleAddUser(selectedStore.id)} disabled={addingUser}>
                      {addingUser ? (t('common.loading') || 'Loading…') : 'Add'}
                    </Button>
                  </div>
                  <div className="border rounded divide-y">
                    {storeUsers.length === 0 && <p className="p-3 text-muted-foreground text-sm">No users yet</p>}
                    {storeUsers
                      .filter(u =>
                        (u.name || '').toLowerCase().includes(userSearch.toLowerCase()) ||
                        (u.email || '').toLowerCase().includes(userSearch.toLowerCase())
                      )
                      .map(u => {
                        // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
                        // 🔒 Determine if current user can edit/delete this user
                        // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
                        // v0.4.9.2.4c5: isSuperadmin / isSadmin are now
                        // component-level (the create-user form needs them
                        // outside this map), so redeclaring them here would
                        // only shadow. isCadmin stays local.
                        const isCadmin = user?.role === 'cadmin';
                        const isTargetSadmin = u.role === 'sadmin';
                        const isTargetSuperadmin = u.role === 'superadmin';
                        const isTargetPrimary = u.is_primary_sadmin || false;
                        const isSelf = u.id === user?.id;

                        // 🔒 SADMIN can only edit/delete Guards, Admins, Cadmins (NOT SADMIN, NOT SUPERADMIN, NOT self)
                        const canEdit = isSuperadmin || 
                          (isSadmin && !isTargetSadmin && !isTargetSuperadmin && !isTargetPrimary && !isSelf);

                        const canDelete = isSuperadmin || 
                          (isSadmin && !isTargetSadmin && !isTargetSuperadmin && !isTargetPrimary && !isSelf);
                        
                        // ✅ ADD THIS – showResetPassword definition
                        let showResetPassword = false;
                        if (isSuperadmin) {
                          showResetPassword = true;
                        } else if (isSadmin) {
                          showResetPassword = !isTargetSadmin && !isTargetSuperadmin && !isTargetPrimary && !isSelf;
                        } else if (isCadmin) {
                          showResetPassword = u.role === 'guard' || u.role === 'admin';
                        }

                        // v0.4.9.2.4c5: the key must be STABLE. It previously
                        // included `editingUserId`, so clicking Edit changed
                        // every row's key and React remounted the whole list —
                        // the form jumped and lost focus. A row's identity is
                        // its user id, nothing else.
                        return (
                          <div key={`user-${u.id}`} className="flex items-center justify-between p-3">
                            {editingUserId === u.id ? (
                              // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
                              // 📝 EDIT FORM
                              // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
                              <div className="flex-1 space-y-2">
                                <Input
                                  placeholder="Name"
                                  value={editName}
                                  onChange={(e) => setEditName(e.target.value)}
                                />
                                <Input
                                  placeholder="Email"
                                  value={editEmail}
                                  onChange={(e) => setEditEmail(e.target.value)}
                                />
                                {/* Store Role */}
                                <div>
                                  <label className="text-xs text-muted-foreground">Store Role</label>
                                  <select
                                    value={editRole}
                                    onChange={(e) => setEditRole(e.target.value as 'guard' | 'admin')}
                                    className="w-full p-2 border rounded mt-1"
                                  >
                                    <option value="guard">Guard</option>
                                    {(isSuperadmin || isSadmin) && <option value="admin">Admin</option>}
                                  </select>
                                </div>

                                {/* Global Role - Only show for SADMIN/SUPERADMIN */}
                                {(isSuperadmin || isSadmin) && (
                                  <div>
                                    <label className="text-xs text-muted-foreground">Global Role</label>
                                    <select
                                      value={editGlobalRole}
                                      onChange={(e) => {
                                        const newGlobalRole = e.target.value as 'guard' | 'admin' | 'cadmin' | 'sadmin' | 'tadmin' | 'superadmin';
                                        setEditGlobalRole(newGlobalRole);
                                        if (newGlobalRole === 'sadmin' || newGlobalRole === 'cadmin' || newGlobalRole === 'superadmin' || newGlobalRole === 'tadmin') {
                                          setEditRole('admin');
                                        } else if (newGlobalRole === 'guard') {
                                          setEditRole('guard');
                                        } else if (newGlobalRole === 'admin') {
                                          setEditRole('admin');
                                        }
                                      }}
                                      className="w-full p-2 border rounded mt-1"
                                    >
                                      <option value="guard">Guard</option>
                                      <option value="admin">Admin</option>
                                      <option value="cadmin">Chief Admin</option>
                                      {/* 🔒 Only PRIMARY SADMIN or SUPERADMIN can assign SADMIN/Superadmin roles.
                                          Cast user to any for `is_primary_sadmin` — the auth User type
                                          (../context/AuthContext.tsx) doesn't declare this field but
                                          the backend JWT includes it for primary sadmins. The longer-
                                          term fix is to add `is_primary_sadmin?: boolean` to the
                                          User interface in AuthContext.tsx and drop these casts. */}
                                      {((user as any)?.role === 'superadmin' || (user as any)?.is_primary_sadmin) && (
                                        <>
                                          <option value="sadmin">SAdmin</option>
                                          {/* v0.4.9.2.4c5: TAdmin = Technical Admin.
                                              Provisions Edge Servers + installs Cameras.
                                              Global scope, parallel to SAdmin in rank.
                                              Only SuperAdmin may assign or revoke it. */}
                                          <option value="tadmin">TAdmin (Technical Admin)</option>
                                          <option value="superadmin">Super Admin</option>
                                        </>
                                      )}
                                    </select>
                                    <p className="text-[10px] text-muted-foreground mt-1">
                                      {(user as any)?.role === 'superadmin' || (user as any)?.is_primary_sadmin
                                        ? 'You can assign SAdmin and Super Admin roles.'
                                        : 'Only SUPERADMIN or Primary SAdmin can assign these roles.'}
                                    </p>
                                  </div>
                                )}

                                {/* Training toggle - admin role only, and never
                                    for a TAdmin. The TAdmin global role maps to
                                    per-store 'admin', so editRole === 'admin'
                                    was true whenever a TAdmin opened a TAdmin or
                                    Admin record and the toggle appeared. Training
                                    Mode is a client-facing feature
                                    (clients.training_mode_enabled); a TAdmin is a
                                    technical role and must not toggle it. */}
                                {editRole === 'admin' && !isTadmin && (
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="checkbox"
                                      id="training_enabled_edit"
                                      checked={editTrainingEnabled}
                                      onChange={(e) => setEditTrainingEnabled(e.target.checked)}
                                    />
                                    <label htmlFor="training_enabled_edit" className="text-sm">Training Module</label>
                                  </div>
                                )}

                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    onClick={async () => {
                                      try {
                                        // 🔒 Prevent SADMIN from modifying SUPERADMIN or SADMIN
                                        if ((u.role === 'superadmin' || u.role === 'sadmin') && !isSuperadmin) {
                                          alert('You cannot modify Super Admin or SAdmin users.');
                                          return;
                                        }

                                        let storeRole: 'guard' | 'admin';
                                        if (editRole === 'cadmin') {
                                          storeRole = 'admin';
                                        } else if (editRole === 'admin') {
                                          storeRole = 'admin';
                                        } else {
                                          storeRole = 'guard';
                                        }
                                        if (editGlobalRole === 'sadmin' || editGlobalRole === 'cadmin' || editGlobalRole === 'tadmin') {
                                          storeRole = 'admin';
                                        }

                                        const globalRole = isSuperadmin ? editGlobalRole : undefined;

                                        await api.updateStoreUser(selectedStore!.id, u.id, {
                                          name: editName,
                                          email: editEmail,
                                          role_in_store: storeRole,
                                          role: globalRole,
                                          training_enabled: editTrainingEnabled,
                                        });
                                        
                                        setEditingUserId(null);
                                        // Refresh user list
                                        const users = await api.getStoreUsers(selectedStore!.id);
                                        setStoreUsers(users);
                                        
                                        // Clear edit form
                                        setEditName('');
                                        setEditEmail('');
                                        setEditRole('guard');
                                        setEditGlobalRole('guard');
                                        setEditTrainingEnabled(false);
                                      } catch (err: any) {
                                        setModalError(err.message);
                                      }
                                    }}
                                  >
                                    Save
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={() => {
                                    setEditingUserId(null);
                                    setEditName('');
                                    setEditEmail('');
                                    setEditRole('guard');
                                    setEditGlobalRole('guard');
                                    setEditTrainingEnabled(false);
                                  }}>
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
                              // 👤 USER DISPLAY (when not editing)
                              // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
                              <>
                                <div>
                                  <p className="font-medium">
                                    {u.name}
                                    {u.is_primary_sadmin && (
                                      <span className="ml-2 text-xs text-foreground font-semibold">🔒 Primary</span>
                                    )}
                                    {u.role === 'superadmin' && (
                                      <span className="ml-2 text-xs text-destructive font-semibold">🔒 Super Admin</span>
                                    )}
                                    {u.role === 'sadmin' && (
                                      <span className="ml-2 text-xs text-purple-600 font-semibold">🔒 SAdmin</span>
                                    )}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {u.email} · {u.role_in_store}
                                    {u.role !== u.role_in_store && (
                                      <span className="ml-1 font-medium text-blue-600 dark:text-blue-400">
                                        (Global: {u.role})
                                      </span>
                                    )}
                                  </p>
                                </div>

                                {/* 🔒 Only show actions if user can edit/delete */}
                                {(canEdit || canDelete) && (isSuperadmin || u.id !== user?.id) && (
                                  <div className="flex flex-col gap-2">
                                    <div className="flex gap-2">
                                      {/* Edit button – SUPERADMIN can edit themselves */}
                                      {canEdit && (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => {
                                            const currentUser = storeUsers.find(user => user.id === u.id);
                                            if (!currentUser) return;
                                            
                                            setEditingUserId(currentUser.id);
                                            setEditName(currentUser.name || '');
                                            setEditEmail(currentUser.email || '');
                                            setEditRole(currentUser.role_in_store as 'guard' | 'admin');
                                            setEditGlobalRole(currentUser.role as 'guard' | 'admin' | 'cadmin' | 'sadmin' | 'tadmin' | 'superadmin');
                                            setEditTrainingEnabled(currentUser.training_enabled || false);
                                          }}
                                        >
                                          Edit
                                        </Button>
                                      )}
                                      
                                      {/* Delete button – never delete yourself */}
                                      {u.id !== user?.id && canDelete && (
                                        <Button
                                          size="sm"
                                          variant="destructive"
                                          onClick={() => {
                                            if (u.is_primary_sadmin) {
                                              alert('The primary SADMIN cannot be deleted.');
                                              return;
                                            }
                                            if (u.role === 'superadmin') {
                                              alert('Super Admin users cannot be deleted.');
                                              return;
                                            }
                                            if (u.role === 'sadmin' && !isSuperadmin) {
                                              alert('SAdmin users cannot be deleted by SADMIN.');
                                              return;
                                            }
                                            if (u.role === 'cadmin' && isCadmin) {
                                              alert('Cadmin users cannot be deleted by Cadmin.');
                                              return;
                                            }
                                            handleRemoveUser(selectedStore!.id, u.id);
                                          }}
                                        >
                                          Remove
                                        </Button>
                                      )}
                                      
                                      {/* v0.4.6i: Reset Password / Reset Device button.
                                          Opens AdminResetDialog with two options
                                          (reset password, reset device) instead of the
                                          single confirm + alert flow. */}
                                      {showResetPassword && (
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          title={t('admin.resetPassword')}
                                          onClick={() => setResetTarget({
                                            userId: u.id,
                                            userName: u.name || u.email,
                                            userEmail: u.email,
                                          })}
                                        >
                                          🔑
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* 🔒 If user cannot edit/delete, show a lock icon */}
                                {!canEdit && !canDelete && u.id !== user?.id && !isSuperadmin && (
                                  <div className="text-xs text-muted-foreground flex items-center gap-1">
                                    <span>🔒</span> Protected
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </div>

                {/* Authorized Persons Section */}
                <div className="space-y-4 border-b pb-4">
                  <h3 className="font-semibold text-lg">Authorized Persons</h3>
                  <AuthorizedPersonsManager
                    storeId={selectedStore.id}
                    edgeIp={selectedStore.edge_server_ip}
                    edgePort={selectedStore.edge_server_port ?? 3002}
                  />
                </div>

                {/* Authorized License Plates Section */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Authorized License Plates</h3>
                  <AuthorizedPlatesManager
                    storeId={selectedStore.id}
                  />
                </div>

                {/* POI Management */}
                <div className="space-y-4 border-b pb-4">
                  <h3 className="font-semibold text-lg">POIs</h3>
                  <PoiListManager storeId={selectedStore.id} refreshKey={poiListRefresh} />
                </div>

                {/* Bulk Upload POIs */}
                <div className="space-y-4 border-t pt-4">
                  <h3 className="font-semibold text-lg">Bulk Upload POIs</h3>
                    <BulkPOIUpload
                      storeId={selectedStore.id}
                      storeInfo={{ country_code: selectedStore.country_code, city_code: selectedStore.city_code }}
                      existingCodes={storePoiCodes}
                      onSuccess={() => {
                        api.getStorePois(selectedStore.id).then(data => {
                          setStorePoiCodes(data.map((p: any) => p.code));
                        }).catch(err => {
                          console.error(err);
                          setStorePoiCodes([]);
                        });
                        setPoiListRefresh(k => k + 1);
                      }}
                      edgeIp={selectedStore.edge_server_ip}
                      edgePort={selectedStore.edge_server_port ?? 3002}
                    />
                </div>
                </div>)}
                {storeTab === 'provisioning' && (<div className="contents">
                {/* Camera Management */}
                <div className="space-y-4 border-t pt-4">
                  <div className="flex justify-between items-center">
                    <h3 className="font-semibold text-lg">Edge Cameras</h3>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={async () => {
                        if (!selectedStore) return;
                        try {
                          const data = await api.getCameras(selectedStore.id);
                          setCameras((data || []).map((c: any) => ({
                            id: c.id,
                            name: c.name,
                            rtsp_url: c.rtsp_url,
                            role: c.role || 'person',
                            location: c.location || '',
                            poi_similarity_threshold: c.poi_similarity_threshold,
                            poi_min_face_score: c.poi_min_face_score,
                            poi_cooldown_sec: c.poi_cooldown_sec,
                            ac_similarity_threshold: c.ac_similarity_threshold,
                            ac_min_face_score: c.ac_min_face_score,
                            ac_cooldown_sec: c.ac_cooldown_sec,
                            min_bbox_ratio: c.min_bbox_ratio,
                            max_bbox_ratio: c.max_bbox_ratio,
                            capture_cooldown_sec: c.capture_cooldown_sec,
                          })));
                        } catch (err) {
                          console.error('[iSentinel360] Refresh cameras failed:', err);
                        }
                      }}>Refresh</Button>
                      <Button size="sm" onClick={() => {
                        setEditingCamera(null);
                        setCameraForm({ id: crypto.randomUUID(), name: '', rtsp_url: '', role: 'access_person', location: '' });
                        setShowCameraModal(true);
                      }}>
                        <Plus className="w-4 h-4 mr-1" /> Add Camera
                      </Button>
                    </div>
                  </div>
                  {cameras.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No cameras configured.</p>
                  ) : (
                    <div className="grid gap-2">
                      {cameras.map(cam => (
                        <div key={cam.id} className="border rounded-lg p-3 flex justify-between items-center">
                          <div>
                            <p className="font-mono text-sm font-medium">{cam.name}</p>
                            <p className="text-xs text-muted-foreground">Role: {cam.role} | Location: {cam.location}</p>
                            <p className="text-xs text-muted-foreground truncate max-w-md">{cam.rtsp_url}</p>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => {
                              setEditingCamera(cam);
                              setCameraForm({
                                id: cam.id,
                                name: cam.name,
                                rtsp_url: cam.rtsp_url,
                                role: cam.role,
                                location: cam.location || '',
                                // POI settings
                                poi_similarity_threshold: cam.poi_similarity_threshold ?? 0.47,
                                poi_min_face_score: cam.poi_min_face_score ?? 0.86,
                                poi_cooldown_sec: cam.poi_cooldown_sec ?? 30,
                                // AC settings
                                ac_similarity_threshold: cam.ac_similarity_threshold ?? 0.47,
                                ac_min_face_score: cam.ac_min_face_score ?? 0.86,
                                ac_cooldown_sec: cam.ac_cooldown_sec ?? 45,
                                // Shared settings
                                min_bbox_ratio: cam.min_bbox_ratio ?? 0.55,
                                max_bbox_ratio: cam.max_bbox_ratio ?? 1.65,
                                capture_cooldown_sec: cam.capture_cooldown_sec ?? 60,
                              });
                              setShowCameraModal(true);
                            }}>Edit</Button>
                            <Button size="sm" variant="destructive" onClick={async () => {
                              if (!confirm('Delete this camera?')) return;
                              try {
                                await api.deleteCamera(cam.id);
                                setCameras(cameras.filter(c => c.id !== cam.id));
                              } catch (err: any) {
                                console.error('[iSentinel360] Delete camera failed:', err);
                                alert('Failed to delete camera: ' + (err?.message || 'unknown error'));
                              }
                            }}>Delete</Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Camera Modal */}
                <Dialog open={showCameraModal} onOpenChange={setShowCameraModal}>
                  <DialogContent className="bg-card dark:bg-card max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{editingCamera ? 'Edit Camera' : 'Add Camera'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      {/* Basic Settings */}
                      <div>
                        <Label className="mb-2">Camera Name</Label>
                        <Input
                          value={cameraForm.name}
                          onChange={e => setCameraForm({ ...cameraForm, name: e.target.value })}
                          placeholder="Main Entrance"
                        />
                      </div>
                      <div>
                        <Label className="mb-2">RTSP URL</Label>
                        <Input
                          value={cameraForm.rtsp_url}
                          onChange={e => setCameraForm({ ...cameraForm, rtsp_url: e.target.value })}
                          placeholder="rtsp://username:password@ip:port/stream"
                        />
                      </div>
                      <div>
                        <Label className="mb-2">Role</Label>
                        <select
                          className="w-full p-2 border rounded bg-background"
                          value={cameraForm.role}
                          onChange={e => {
                            const newRole = e.target.value as 'access_person' | 'poi_person' | 'poi_vehicle' | 'poi_mix' | 'access_vehicle' | 'drone_detection';
                            setCameraForm({ 
                              ...cameraForm, 
                              role: newRole,
                              // Reset irrelevant settings when role changes to avoid confusion
                              poi_similarity_threshold: newRole === 'poi_person' ? (cameraForm.poi_similarity_threshold ?? 0.47) : undefined,
                              poi_min_face_score: newRole === 'poi_person' ? (cameraForm.poi_min_face_score ?? 0.86) : undefined,
                              poi_cooldown_sec: newRole === 'poi_person' ? (cameraForm.poi_cooldown_sec ?? 30) : undefined,
                              ac_similarity_threshold: newRole === 'access_person' || newRole === 'access_vehicle' ? (cameraForm.ac_similarity_threshold ?? 0.47) : undefined,
                              ac_min_face_score: newRole === 'access_person' || newRole === 'access_vehicle' ? (cameraForm.ac_min_face_score ?? 0.86) : undefined,
                              ac_cooldown_sec: newRole === 'access_person' || newRole === 'access_vehicle' ? (cameraForm.ac_cooldown_sec ?? 30) : undefined,
                            });
                          }}
                        >
                          <option value="access_person">Access Control – Personnel</option>
                          <option value="poi_person">POI (Person of Interest)</option>
                          <option value="poi_mix">POI – Person + Vehicle (parking/gate)</option>
                          <option value="poi_vehicle">VOI (Vehicle of Interest)</option>
                          <option value="access_vehicle">Access Control – Vehicles</option>
                          <option value="drone_detection">Drone Detection (Future)</option>
                        </select>
                      </div>
                      <div>
                        <Label className="mb-2">Location</Label>
                        <Input
                          value={cameraForm.location}
                          onChange={e => setCameraForm({ ...cameraForm, location: e.target.value })}
                          placeholder="Entrance, Parking, etc."
                        />
                      </div>

                      {/* Advanced Settings - Role-Specific */}
                      <details className="border rounded p-3">
                        <summary className="cursor-pointer font-semibold text-sm text-muted-foreground hover:text-foreground">
                          ⚙️ Advanced Settings
                        </summary>
                        <div className="space-y-4 mt-4">
                          
                          {/* ---- POI SETTINGS (only for poi_person and poi_mix) ---- */}
                          {(cameraForm.role === 'poi_person' || cameraForm.role === 'poi_mix') && (
                            <div className="border-l-2 border-blue-400 pl-3">
                              <h4 className="font-semibold text-sm text-blue-600 dark:text-blue-400 mb-2">POI Settings</h4>
                              
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                  <Label className="text-xs">Similarity Threshold</Label>
                                  <Input
                                    type="number"
                                    min={0.01}
                                    max={1.0}
                                    step={0.01}
                                    value={cameraForm.poi_similarity_threshold ?? 0.47}
                                    onChange={e => setCameraForm({ 
                                      ...cameraForm, 
                                      poi_similarity_threshold: parseFloat(e.target.value) || 0.47 
                                    })}
                                  />
                                  <p className="text-[10px] text-muted-foreground">Min similarity to trigger POI alert</p>
                                </div>
                                
                                <div>
                                  <Label className="text-xs">Min Face Score</Label>
                                  <Input
                                    type="number"
                                    min={0.1}
                                    max={1.0}
                                    step={0.01}
                                    value={cameraForm.poi_min_face_score ?? 0.86}
                                    onChange={e => setCameraForm({ 
                                      ...cameraForm, 
                                      poi_min_face_score: parseFloat(e.target.value) || 0.86 
                                    })}
                                  />
                                  <p className="text-[10px] text-muted-foreground">Min face detection confidence</p>
                                </div>
                                
                                <div>
                                  <Label className="text-xs">Alert Cooldown (seconds)</Label>
                                  <Input
                                    type="number"
                                    min={5}
                                    max={300}
                                    value={cameraForm.poi_cooldown_sec ?? 30}
                                    onChange={e => setCameraForm({ 
                                      ...cameraForm, 
                                      poi_cooldown_sec: parseInt(e.target.value) || 30 
                                    })}
                                  />
                                  <p className="text-[10px] text-muted-foreground">Min time between alerts for same POI</p>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* ---- ACCESS CONTROL SETTINGS (only for access_person or access_vehicle) ---- */}
                          {(cameraForm.role === 'access_person' || cameraForm.role === 'access_vehicle' || cameraForm.role === 'poi_vehicle' || cameraForm.role === 'poi_mix') && (
                            <div className="border-l-2 border-primary pl-3">
                              <h4 className="font-semibold text-sm text-primary dark:text-primary mb-2">Access Control Settings</h4>
                              
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                  <Label className="text-xs">Similarity Threshold</Label>
                                  <Input
                                    type="number"
                                    min={0.01}
                                    max={1.0}
                                    step={0.01}
                                    value={cameraForm.ac_similarity_threshold ?? 0.47}
                                    onChange={e => setCameraForm({ 
                                      ...cameraForm, 
                                      ac_similarity_threshold: parseFloat(e.target.value) || 0.47 
                                    })}
                                  />
                                  <p className="text-[10px] text-muted-foreground">Min similarity to recognize authorized person/vehicle</p>
                                </div>
                                
                                <div>
                                  <Label className="text-xs">Min Face Score</Label>
                                  <Input
                                    type="number"
                                    min={0.1}
                                    max={1.0}
                                    step={0.01}
                                    value={cameraForm.ac_min_face_score ?? 0.86}
                                    onChange={e => setCameraForm({ 
                                      ...cameraForm, 
                                      ac_min_face_score: parseFloat(e.target.value) || 0.86 
                                    })}
                                  />
                                  <p className="text-[10px] text-muted-foreground">Min face detection confidence for AC</p>
                                </div>
                                
                                <div>
                                  <Label className="text-xs">Alert Cooldown (seconds)</Label>
                                  <Input
                                    type="number"
                                    min={5}
                                    max={300}
                                    value={cameraForm.ac_cooldown_sec ?? 30}
                                    onChange={e => setCameraForm({ 
                                      ...cameraForm, 
                                      ac_cooldown_sec: parseInt(e.target.value) || 30 
                                    })}
                                  />
                                  <p className="text-[10px] text-muted-foreground">Min time between unauthorized person alerts</p>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* ---- DRONE DETECTION (future placeholder) ---- */}
                          {(cameraForm.role === 'drone_detection') && (
                            <div className="border-l-2 border-orange-400 pl-3">
                              <h4 className="font-semibold text-sm text-orange-600 dark:text-orange-400 mb-2">Drone Detection</h4>
                              <p className="text-sm text-muted-foreground">Drone detection settings will be available in a future update.</p>
                            </div>
                          )}

                          {/* ---- SHARED SETTINGS (always visible) ---- */}
                          <div className="border-l-2 border-purple-400 pl-3">
                            <h4 className="font-semibold text-sm text-purple-600 dark:text-purple-400 mb-2">Shared Settings</h4>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs">Min BBox Ratio</Label>
                                <Input
                                  type="number"
                                  min={0.1}
                                  max={3.0}
                                  step={0.05}
                                  value={cameraForm.min_bbox_ratio ?? 0.55}
                                  onChange={e => setCameraForm({ 
                                    ...cameraForm, 
                                    min_bbox_ratio: parseFloat(e.target.value) || 0.55 
                                  })}
                                />
                                <p className="text-[10px] text-muted-foreground">Min width/height ratio for face</p>
                              </div>
                              
                              <div>
                                <Label className="text-xs">Max BBox Ratio</Label>
                                <Input
                                  type="number"
                                  min={0.1}
                                  max={3.0}
                                  step={0.05}
                                  value={cameraForm.max_bbox_ratio ?? 1.65}
                                  onChange={e => setCameraForm({ 
                                    ...cameraForm, 
                                    max_bbox_ratio: parseFloat(e.target.value) || 1.65 
                                  })}
                                />
                                <p className="text-[10px] text-muted-foreground">Max width/height ratio for face</p>
                              </div>
                              
                              <div>
                                <Label className="text-xs">Capture Cooldown (seconds)</Label>
                                <Input
                                  type="number"
                                  min={10}
                                  max={300}
                                  value={cameraForm.capture_cooldown_sec ?? 60}
                                  onChange={e => setCameraForm({ 
                                    ...cameraForm, 
                                    capture_cooldown_sec: parseInt(e.target.value) || 60 
                                  })}
                                />
                                <p className="text-[10px] text-muted-foreground">Min time between same face captures</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </details>

                      <div className="flex justify-end gap-2 pt-4 border-t">
                        <Button variant="outline" onClick={() => setShowCameraModal(false)}>Cancel</Button>
                        <Button onClick={async () => {
                          if (!selectedStore) return;
                          try {
                            // Build payload with all settings
                            const payload: any = {
                              name: cameraForm.name,
                              rtsp_url: cameraForm.rtsp_url,
                              role: cameraForm.role,
                              location: cameraForm.location,
                              // Shared settings
                              min_bbox_ratio: cameraForm.min_bbox_ratio ?? 0.55,
                              max_bbox_ratio: cameraForm.max_bbox_ratio ?? 1.65,
                              capture_cooldown_sec: cameraForm.capture_cooldown_sec ?? 60,
                            };

                            // Add role-specific settings
                            if (cameraForm.role === 'poi_person' || cameraForm.role === 'poi_mix') {
                              payload.poi_similarity_threshold = cameraForm.poi_similarity_threshold ?? 0.47;
                              payload.poi_min_face_score = cameraForm.poi_min_face_score ?? 0.86;
                              payload.poi_cooldown_sec = cameraForm.poi_cooldown_sec ?? 30;
                            } else if (cameraForm.role === 'access_person' || cameraForm.role === 'access_vehicle' || cameraForm.role === 'poi_vehicle' ) {
                              payload.ac_similarity_threshold = cameraForm.ac_similarity_threshold ?? 0.47;
                              payload.ac_min_face_score = cameraForm.ac_min_face_score ?? 0.86;
                              payload.ac_cooldown_sec = cameraForm.ac_cooldown_sec ?? 30;
                            }

                            if (editingCamera) {
                              const updated = await api.updateCamera(editingCamera.id, payload);
                              setCameras(cameras.map(c =>
                                c.id === editingCamera.id
                                  ? { 
                                      ...c, 
                                      name: updated.name, 
                                      rtsp_url: updated.rtsp_url, 
                                      role: updated.role, 
                                      location: updated.location || '',
                                      // Store settings
                                      poi_similarity_threshold: updated.poi_similarity_threshold,
                                      poi_min_face_score: updated.poi_min_face_score,
                                      poi_cooldown_sec: updated.poi_cooldown_sec,
                                      ac_similarity_threshold: updated.ac_similarity_threshold,
                                      ac_min_face_score: updated.ac_min_face_score,
                                      ac_cooldown_sec: updated.ac_cooldown_sec,
                                      min_bbox_ratio: updated.min_bbox_ratio,
                                      max_bbox_ratio: updated.max_bbox_ratio,
                                      capture_cooldown_sec: updated.capture_cooldown_sec,
                                    }
                                  : c
                              ));
                            } else {
                              const created = await api.createCamera({
                                storeId: selectedStore.id,
                                ...payload,
                              });
                                setCameras([...cameras, {
                                  id: created.id,
                                  name: created.name,
                                  rtsp_url: created.rtsp_url,
                                  role: created.role || 'access_person',
                                  location: created.location || '',
                                  poi_similarity_threshold: created.poi_similarity_threshold ?? 0.47,
                                  poi_min_face_score: created.poi_min_face_score ?? 0.86,
                                  poi_cooldown_sec: created.poi_cooldown_sec ?? 30,
                                  ac_similarity_threshold: created.ac_similarity_threshold ?? 0.47,
                                  ac_min_face_score: created.ac_min_face_score ?? 0.86,
                                  ac_cooldown_sec: created.ac_cooldown_sec ?? 45,
                                  min_bbox_ratio: created.min_bbox_ratio ?? 0.55,
                                  max_bbox_ratio: created.max_bbox_ratio ?? 1.65,
                                  capture_cooldown_sec: created.capture_cooldown_sec ?? 60,
                                }]);
                            }
                            setShowCameraModal(false);
                          } catch (err: any) {
                            console.error('[iSentinel360] Save camera failed:', err);
                            alert('Failed to save camera: ' + (err?.message || 'unknown error'));
                          }
                        }}>Save</Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
                </div>)}

              </>
            )}


          </DialogContent>
        </Dialog>

        {/* v0.4.6i: reset-access dialog. Same component is used at
            the commerce-user reset site and the civil-respondent
            reset site. For civil-respondent users, the dialog uses
            the civil-org-specific reset-password endpoint. */}
        {resetTarget && (
          <AdminResetDialog
            open={!!resetTarget}
            onClose={() => setResetTarget(null)}
            userId={resetTarget.userId}
            userName={resetTarget.userName}
            userEmail={resetTarget.userEmail}
            resetPassword={managingCivilOrg
              ? async () => {
                  const r = await api.resetCivilRespondentUserPassword(managingCivilOrg.id, resetTarget.userId);
                  return { success: r.success, emailSent: r.emailSent };
                }
              : undefined}
          />
        )}

        {/* ---- OPSEC tab ---- */}
        {activeTab === 'opsec' && (
          <div className="space-y-6">
            {/* Header strip with refresh */}
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5" />
                  {t('network.opsecTitle') || 'OPSEC Monitoring'}
                </h2>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('network.opsecSubtitle') || 'Live server & firewall status at a glance. Open the full dashboard for detail.'}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={loadOpsecSnapshot}
                disabled={opsecLoading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${opsecLoading ? 'animate-spin' : ''}`} />
                {t('common.refresh') || 'Refresh'}
              </Button>
            </div>

            {/* 4 stat cards */}
            {opsecLoading && !opsecSnapshot ? (
              <p className="text-sm text-muted-foreground">{t('network.opsecLoading') || 'Loading OPSEC status...'}</p>
            ) : opsecError ? (
              <div className="p-3 bg-destructive/10 text-destructive rounded text-sm">
                {opsecError}
              </div>
            ) : opsecSnapshot ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {/* UFW blocks active */}
                <Card className="p-4">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">
                    {t('network.opsecActiveBlocks') || 'Active UFW blocks'}
                  </p>
                  <p className="text-2xl font-bold text-foreground mt-1 flex items-center gap-2">
                    <Ban className="w-5 h-5 text-destructive" />
                    {opsecSnapshot?.blockedIPs?.ufw?.length ?? 0}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    {t('network.opsecUfwHint') || 'Rules active in firewall'}
                  </p>
                </Card>
                {/* Nginx denies */}
                <Card className="p-4">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">
                    {t('network.opsecNginxDenies') || 'Nginx denies'}
                  </p>
                  <p className="text-2xl font-bold text-foreground mt-1 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-primary" />
                    {opsecSnapshot?.blockedIPs?.nginx?.length ?? 0}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    {t('network.opsecNginxHint') || 'IPs in deny_ips.conf'}
                  </p>
                </Card>
                {/* Whitelisted */}
                <Card className="p-4">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">
                    {t('network.opsecWhitelisted') || 'Whitelisted'}
                  </p>
                  <p className="text-2xl font-bold text-foreground mt-1 flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-primary" />
                    {opsecSnapshot?.whitelistedIPs?.length ?? 0}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    {t('network.opsecWhitelistHint') || 'UFW ALLOW rules'}
                  </p>
                </Card>
                {/* Suspicious last hour */}
                <Card className="p-4">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">
                    {t('network.opsecSuspiciousLastHour') || 'Suspicious (1h)'}
                  </p>
                  <p className={`text-2xl font-bold mt-1 flex items-center gap-2 ${
                    (opsecSnapshot?.accessStats?.suspiciousPatterns?.length ?? 0) > 0
                      ? 'text-destructive'
                      : 'text-foreground'
                  }`}>
                    <Activity className="w-5 h-5" />
                    {opsecSnapshot?.accessStats?.suspiciousPatterns?.length ?? 0}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    {t('network.opsecSuspiciousHint') || '403s + bad patterns'}
                  </p>
                </Card>
              </div>
            ) : null}

            {/* Quick block IP */}
            <Card className="p-4">
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Ban className="w-4 h-4 text-destructive" />
                {t('network.opsecQuickBlock') || 'Quick block IP'}
              </h3>
              <form onSubmit={handleOpsecQuickBlock} className="flex flex-col sm:flex-row gap-2">
                <Input
                  type="text"
                  value={opsecBlockIp}
                  onChange={(e) => setOpsecBlockIp(e.target.value)}
                  placeholder={t('network.opsecBlockIpPlaceholder') || '1.2.3.4'}
                  className="flex-1"
                  inputMode="numeric"
                  pattern="^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$"
                />
                <Input
                  type="text"
                  value={opsecBlockReason}
                  onChange={(e) => setOpsecBlockReason(e.target.value)}
                  placeholder={t('network.opsecBlockReasonPlaceholder') || 'Reason (optional)'}
                  className="flex-1"
                />
                <Button type="submit" disabled={opsecBlocking || !opsecBlockIp.trim()}>
                  <Ban className="w-4 h-4 mr-2" />
                  {opsecBlocking ? (t('common.loading') || 'Loading...') : (t('network.opsecBlockButton') || 'Block')}
                </Button>
              </form>
              {opsecBlockMessage && (
                <p className={`mt-2 text-sm ${opsecBlockMessage.kind === 'ok' ? 'text-primary' : 'text-destructive'}`}>
                  {opsecBlockMessage.kind === 'ok' ? '✓ ' : '✗ '}
                  {opsecBlockMessage.text}
                </p>
              )}
            </Card>

            {/* Open full dashboard */}
            <div className="flex justify-end">
              <Button onClick={() => navigate('/opsec#overview')}>
                <Eye className="w-4 h-4 mr-2" />
                {t('network.opsecOpenFull') || 'View full OPSEC dashboard →'}
              </Button>
            </div>
          </div>
        )}

        {/* ---- AI Training tab (v0.4.9.4.6) ----
            Same guard as the tab button, so a role change (or a stale
            localStorage user) cannot leave the panel on screen for an
            account the backend would 403. requireAiTraining is the real
            gate; this is defence in depth. */}
        {activeTab === 'aiTraining' && (isSuperadmin || isSadmin) && (
          <AiTraining />
        )}

      </div>
    </div>
  );
}